import type { MessagePack } from "./en.ts";

/**
 * The Spanish pack.
 *
 * **Needs a native reviewer before it ships**, on the same terms as
 * [`km.ts`](km.ts): restaurant and POS vocabulary has established trade usage
 * that a plausible-sounding translation misses, and a machine-drafted pack of
 * this size is a draft rather than a shipped locale. `es.status` is
 * `review_required` in the locale registry and stays there until a reviewer
 * signs it off.
 *
 * **Neutral Latin American Spanish, not peninsular.** The US restaurant
 * workforce this pack exists for is overwhelmingly Mexican and Central
 * American, so: `computadora` not `ordenador`, `celular` not `móvil`, and
 * `ustedes` throughout — `vosotros` would read as foreign to every person who
 * will actually use this screen.
 *
 * Two terminology decisions worth stating once, because they are load-bearing
 * across ~1,500 keys:
 *
 *  - **`la caja` is the till**, the physical register a cashier stands at, and
 *    `el terminal` is any device. Spanish has no single word doing the job
 *    English's "till" does here, and mixing the two mid-pack is how a cashier
 *    ends up unsure which screen a message is about.
 *  - **`cuenta` is the bill and `pedido` is the order** — the thing that is
 *    paid versus the thing that is cooked. They diverge at exactly the point
 *    this product cares about (split, merge, hold), so they are never used
 *    interchangeably. `comanda` is reserved for the ticket a station receives.
 */
export const es: MessagePack = {
  "app.name": "TOVI",
  "app.description": "Administración del restaurante",
  "app.operatorName": "TOVI Operator",
  "app.operatorDescription": "Consola de operador de la plataforma",

  "nav.dashboard": "Panel",
  "nav.menu": "Menú",
  "nav.operations": "Operaciones",
  "nav.staff": "Personal",
  "nav.roles": "Puestos",
  "nav.stations": "Estaciones",
  "nav.printers": "Impresoras",
  "nav.pairTill": "Vincular un terminal",
  "nav.printLog": "Registro de impresión",
  "nav.receiptTemplates": "Plantillas de recibo",
  "nav.banners": "Anuncios",
  "nav.notifications": "Avisos",
  "nav.operationLog": "Registro de operaciones",
  "nav.inventory": "Inventario",
  "nav.supplies": "Insumos",
  "nav.suppliers": "Proveedores",
  "nav.warehouses": "Almacenes",
  "nav.units": "Unidades",
  "nav.supplyCategories": "Categorías de insumos",
  "nav.brands": "Marcas",
  "nav.chain": "Cadena",
  "nav.substores": "Sucursales",
  "nav.invoice": "Factura",
  "nav.trading": "Operación",

  "staff.title": "Personal",
  "staff.description": "Quién trabaja aquí, qué puede hacer y quién puede usar la caja.",
  "staff.searchPlaceholder": "Buscar por nombre, usuario o número",
  "staff.allRoles": "Cualquier puesto",
  "staff.noRole": "Sin puesto",
  "staff.allStatuses": "Cualquier estado",
  "staff.tillAccess": "Acceso a la caja",
  "staff.allTillAccess": "Cualquier acceso a la caja",
  "staff.tillAllowed": "Puede usar la caja",
  "staff.tillDenied": "No puede usar la caja",
  "staff.new": "Nuevo empleado",
  "staff.edit": "Editar empleado",
  "staff.record": "Ficha",
  "staff.name": "Nombre",
  "staff.username": "Usuario",
  "staff.number": "Número de empleado",
  "staff.role": "Puesto",
  "staff.cashRegister": "Puede usar la caja",
  "staff.cashRegisterHint": "Es aparte de su puesto. Alguien puede tomar pedidos sin poder cobrar.",
  "staff.posPermissions": "Permisos de caja",
  "staff.inventoryPermissions": "Permisos de inventario",
  "staff.inventoryHint": "Se otorgan aparte del acceso a la caja.",
  "staff.deleteTitle": "¿Dar de baja a este empleado?",
  "staff.deleteBody": "Ya no podrá entrar a la caja. Los pedidos que registró conservan su nombre.",
  "staff.photo": "Foto",
  "staff.photoHint": "Se muestra junto a su nombre en la lista.",
  "staff.photoAdd": "Agregar foto",
  "staff.photoReplace": "Reemplazar foto",
  "staff.photoRemove": "Quitar foto",
  "staff.viewPhoto": "Ver foto",
  "staff.cropTitle": "Recortar foto",
  "staff.cropHint": "Arrastra para reposicionar. Solo se conserva lo que quede dentro del círculo.",
  "staff.zoom": "Zoom",
  "staff.cropConfirm": "Usar esta foto",
  "staff.removePhotoTitle": "¿Quitar esta foto?",
  "staff.removePhotoBody":
    "Sus iniciales se mostrarán en su lugar hasta que alguien agregue una nueva.",

  "staff.setPassword": "Establecer contraseña",
  "staff.password": "Contraseña",
  "staff.passwordConfirm": "Confirmar contraseña",
  "staff.passwordHint":
    "Al menos ocho caracteres. Inician sesión en la app de personal con su usuario y esta contraseña.",
  "staff.passwordMismatch": "Las dos contraseñas no coinciden.",
  "staff.passwordSaved":
    "Contraseña establecida. Se cerró su sesión en todas partes y puede iniciar sesión con la nueva.",

  "roles.title": "Puestos",
  "roles.description": "Lo que puede hacer cada tipo de puesto.",
  "roles.new": "Nuevo puesto",
  "roles.members": "Personas",
  "roles.permissions": "Permisos",
  "roles.seeded": "De fábrica",
  "roles.granted": "{count} otorgados",
  "roles.record": "Puesto",
  "roles.code": "Código",
  "roles.note": "Notas",
  "roles.noneGranted": "No otorga nada.",
  "roles.catalogueFailed":
    "No se pudo cargar el catálogo de permisos, así que cada permiso se muestra por su clave.",

  "stations.title": "Estaciones",
  "stations.description":
    "Donde se hace el trabajo. Los artículos se enrutan a una estación, no a una impresora.",
  "stations.new": "Nueva estación",
  "stations.record": "Ficha",
  "stations.name": "Nombre",
  "stations.nameHint": "Como la llaman quienes trabajan ahí: Caliente, Barra, Postres.",
  "stations.printers": "Impresoras",
  "stations.noPrinters": "Sin impresora",
  "stations.noPrintersHint": "Una estación sin impresora sigue enrutando; no imprime nada.",
  "stations.deleteTitle": "¿Eliminar esta estación?",
  "stations.deleteBody":
    "Solo se puede eliminar una estación a la que no apunte nada: las impresoras que están en ella y los artículos enrutados a ella tienen que moverse primero.",
  "printers.title": "Impresoras",
  "printers.description": "Dispositivos vinculados y en qué estación está cada uno.",
  "printers.new": "Nueva impresora",
  "printers.record": "Ficha",
  "printers.name": "Nombre",
  "printers.model": "Modelo",
  "printers.connection": "Conexión",
  "printers.address": "Dirección",
  "printers.addressHint": "Una IP y un puerto para LAN, o una dirección MAC para Bluetooth.",
  "printers.role": "Imprime",
  "printers.station": "Estación",
  "printers.stationHint": "Todo lo enrutado a esta estación se imprime aquí.",
  "printers.stationNone": "Elija una estación",
  "printers.copies": "Copias",
  "printers.bind": "Vincular",
  "printers.unbind": "Desvincular",
  "printers.status": "Estado",
  "printers.deleteTitle": "¿Quitar esta impresora?",
  "printers.deleteBody":
    "Todo lo enrutado a ella deja de imprimirse. No se puede quitar una impresora que algún artículo señala como su único dispositivo en una estación.",
  "printerRole.receipt": "Recibos",
  "printerRole.station": "Comandas",
  "printerRole.label": "Etiquetas",
  "printerStatus.unbound": "Sin vincular",
  "printerStatus.online": "En línea",
  "printerStatus.offline": "Fuera de línea",
  "printerStatus.error": "Error",

  "printLog.title": "Registro de impresión",
  "printLog.description": "Cada documento enviado a una impresora, y si llegó.",
  "printLog.document": "Documento",
  "printLog.printer": "Impresora",
  "printLog.queued": "En cola",
  "printLog.attempts": "Intentos",
  "printLog.reprint": "Imprimir de nuevo",
  "printLog.failedHint": "Una comanda que falló es un pedido que nadie está preparando.",
  "printLog.record": "Trabajo de impresión",
  "printLog.reference": "Referencia",
  "printLog.printed": "Impreso",
  "printLog.failure": "Por qué falló",
  "printJobStatus.queued": "Esperando",
  "printJobStatus.printing": "Imprimiendo",
  "printJobStatus.printed": "Impreso",
  "printJobStatus.failed": "Falló",
  "printJobStatus.cancelled": "Cancelado",
  "documentType.customer_receipt": "Recibo del cliente",
  "documentType.station_ticket": "Comanda",
  "documentType.label": "Etiqueta",
  "documentType.shift_report": "Reporte de turno",
  "documentType.drawer_kick": "Apertura de caja",

  "templates.title": "Plantillas de recibo",
  "templates.description": "Qué se imprime, en qué idioma y cómo se envía a la impresora.",
  "templates.new": "Nueva plantilla",
  "templates.language": "Idioma",
  "templates.lineWidth": "Caracteres por línea",
  "templates.lineWidthHint": "32 en papel de 58 mm, 48 en papel de 80 mm.",
  "templates.renderMode": "Se envía como",
  "templates.text": "Texto",
  "templates.raster": "Imagen",
  "templates.auto": "Según el idioma",
  "templates.rasterForced": "Este idioma se imprime como imagen",
  "templates.rasterReason.complexScript":
    "{script} no puede imprimirse con las fuentes internas de una impresora térmica, así que este recibo se envía como imagen. Eso es más lento por Bluetooth que por cable.",
  "templates.rasterReason.templateChoice": "Esta plantilla siempre se imprime como imagen.",
  "templates.preview": "Vista previa",
  "templates.showLogo": "Mostrar el logotipo",
  "templates.headerText": "Encabezado",
  "templates.footerText": "Pie",
  "templates.showTaxBreakdown": "Mostrar el desglose de impuestos",
  "templates.showTaxRegistration": "Mostrar los datos fiscales",
  "templates.showItemCodes": "Mostrar los códigos de artículo",
  "templates.showServiceCharge": "Mostrar el cargo por servicio",
  "templates.showSecondCurrency": "Mostrar la segunda moneda",
  "templates.showQrCode": "Mostrar un código QR",

  "banners.title": "Anuncios",
  "banners.description": "Publicidad en el menú de pedidos por QR.",
  "banners.new": "Nuevo anuncio",
  "banners.record": "Ficha",
  "banners.image": "Dirección de la imagen",
  "banners.link": "Enlace",
  "banners.schedule": "Se muestra",
  "banners.startsAt": "Desde",
  "banners.endsAt": "Hasta",
  "banners.live": "Mostrándose ahora",
  "banners.notLive": "Sin mostrarse",
  "banners.deleteTitle": "¿Eliminar este anuncio?",
  "banners.deleteBody": "Sale del menú de pedidos por QR de inmediato.",

  "alerts.title": "Avisos",
  "alerts.description": "De qué se entera el equipo, y cómo.",
  "alerts.push": "Notificación push",
  "alerts.voice": "Se dice en voz alta en la caja",
  "alerts.event": "Evento",
  "alerts.language": "Se dice en",
  "alerts.repeat": "Repetir",
  "alerts.repeatHint": "Una estación ocupada es un lugar ruidoso.",
  "event.order.new": "Llega un pedido nuevo",
  "event.order.paid": "Se paga un pedido",
  "event.order.cancelled": "Se cancela un pedido",
  "event.order.refunded": "Se reembolsa un pedido",
  "event.print.failed": "Un documento no se imprime",
  "event.shift.warning": "Un turno necesita atención",
  "event.stock.sold_out": "Se agota un artículo",

  "pairTill.title": "Vincular un terminal",
  "pairTill.description":
    "Un terminal muestra un código de ocho caracteres. Introdúzcalo aquí para aprobarlo o rechazarlo.",
  "pairTill.codeLabel": "Código del terminal",
  "pairTill.lookUp": "Buscar",
  "pairTill.sessionNotice":
    "El terminal iniciará sesión con su cuenta hasta que alguien inicie sesión en él.",
  "pairTill.approve": "Vincular este terminal",
  "pairTill.deny": "Rechazar",
  "pairTill.approved": "El terminal está vinculado; continuará por sí solo en unos segundos.",
  "pairTill.denied": "Se rechazó el terminal.",
  "pairTill.pairAnother": "Vincular otro terminal",
  "pairTill.notFound": "Ningún terminal muestra ese código.",
  "pairTill.expired": "Ese código ha caducado.",
  "pairTill.forbidden": "No tiene permiso para vincular un terminal.",
  "pairTill.alreadyUsed": "Este código ya se ha usado.",
  "pairTill.claimedElsewhere": "Otra persona ya está viendo este código.",
  "pairTill.identify":
    "Este código identifica a una persona, no a un terminal. No se puede aprobar aquí.",

  "operationLog.title": "Registro de operaciones",
  "operationLog.description":
    "Quién cambió qué, y cuándo. Este registro no se puede editar ni borrar.",
  "operationLog.when": "Cuándo",
  "operationLog.who": "Quién",
  "operationLog.what": "Acción",
  "operationLog.entity": "Afectado",
  "operationLog.fields": "Campos modificados",
  "operationLog.record": "Ficha",
  "operationLog.actorKind": "Actuó como",
  "operationLog.entityKind": "Tipo de ficha",
  "operationLog.recordId": "ID de la ficha",
  "operationLog.fieldsHint":
    "Los valores anteriores y posteriores se guardan con la ficha y no se muestran aquí.",
  "actorKind.merchant": "Comercio",
  "actorKind.operator": "Operador de la plataforma",

  "trading.title": "Operación",
  "trading.description": "Su horario, cómo se comportan los pedidos y qué muestran los terminales.",
  "trading.hours": "Horario",
  "trading.openTime": "Abre",
  "trading.closeTime": "Cierra",
  "trading.hoursHint":
    "Su horario decide a qué día de operación pertenece una venta, y con ello da forma a todos los reportes.",
  "trading.status": "Estado de la sucursal",
  "trading.hoursLog": "Cambios en su horario",
  "trading.ordering": "Pedidos",
  "trading.payFirst": "Los comensales pagan antes de recibir la comida",
  "trading.requirePax": "Preguntar cuántas personas van a comer",
  "trading.cartSharing": "Varios comensales pueden compartir un pedido",
  "trading.autoCancel": "Cancelar los pedidos no aceptados después de",
  "trading.minutes": "minutos",
  "trading.printing": "Impresión",
  "trading.autoPrintReceipt": "Imprimir el recibo automáticamente al cobrar",
  "trading.staffTerminalPrinter": "El terminal del personal imprime directo a la impresora Wi-Fi",
  "trading.staffTerminalPrinterHint":
    "Actívelo solo cuando la caja esté apagada. Con ambos conectados, la caja deja de imprimir recibos.",
  "trading.display": "Qué muestran los terminales",
  "trading.displayTables": "Mostrar el plano de mesas",
  "delivery.title": "Plataformas de reparto",
  "delivery.description": "Las plataformas por las que vende, y lo que se lleva cada una.",
  "delivery.platforms": "Plataformas",
  "delivery.name": "Se muestra como",
  "delivery.commission": "Comisión",
  "delivery.commissionHint": "Un porcentaje. 15 significa 15 %.",
  "delivery.noneInMarket": "Todavía no hay plataformas de reparto disponibles en este mercado.",
  "trading.delivers": "La comida para llevar puede repartirse",
  "trading.deliveryFee": "Costo de reparto",
  "trading.autoAccept": "Aceptar sin preguntar",
  "trading.autoAcceptHint":
    "Un pedido por un canal marcado pasa directo a las estaciones. Todo lo demás espera a que alguien lo revise.",
  "trading.showItemSales": "Mostrar a los comensales cuántos se han vendido",
  "trading.voiceBroadcast": "Leer los pedidos nuevos en voz alta en la caja",
  "orderSource.cashier": "Caja",
  "orderSource.staff_app": "App del personal",
  "orderSource.qr": "Pedido por QR",
  "orderSource.delivery": "Reparto",
  "orderSource.back_office": "Administración",
  "trading.showItemImages": "Mostrar fotos de los artículos en la caja",
  "trading.showRemainingStock": "Mostrar las existencias restantes",
  "trading.geofence": "Distancia para pedir",
  "trading.geofenceHint":
    "Los comensales deben estar a esta distancia en metros para pedir. La más cercana permitida es {min}.",
  "trading.pickupNumbers": "Números de recogida",
  "trading.pickupHint": "Rotan a diario entre estos números. Su mercado permite de {min} a {max}.",
  "chain.title": "Sucursales",
  "chain.description": "Cada local del grupo, y lo que cada uno puede configurar por su cuenta.",
  "chain.new": "Agregar una sucursal",
  "chain.headOffice": "Oficina central",
  "chain.branch": "Sucursal",
  "chain.name": "Nombre",
  "chain.address": "Dirección",
  "chain.slug": "Dirección web",
  "chain.slugHint": "La usa el sitio de pedidos por QR. Minúsculas, letras, números y guiones.",
  "chain.phone": "Teléfono",
  "chain.timezone": "Zona horaria",
  "chain.hours": "Horario",
  "chain.currency": "Moneda",
  "chain.items": "Artículos",
  "chain.tables": "Mesas",
  "chain.staff": "Personal",
  "chain.record": "Ficha",
  "chain.switchTo": "Cambiar a esta sucursal",
  "chain.capabilities": "Lo que esta sucursal puede configurar",
  "chain.capabilitiesHint":
    "Retire uno y la sucursal conserva el permiso pero no puede ejercerlo. Lo configura la oficina central en su lugar.",
  "chain.capabilitiesSaved": "Guardado.",

  "shell.switchStore": "Cambiar de sucursal",
  "nav.chainMenu": "Envío de menú",
  "nav.chainUsers": "Acceso a sucursales",
  "action.done": "Listo",
  "chainMenu.title": "Menú de la oficina central",
  "chainMenu.description":
    "El menú que gestiona la oficina central. Envíe un artículo a un local y ambos quedan vinculados, hasta que ese local edite su propia copia.",
  "chainMenu.sku": "SKU",
  "chainMenu.categories": "Categorías",
  "chainMenu.price": "Precio",
  "chainMenu.status": "Estado",
  "chainMenu.pushedTo": "Locales",
  "chainMenu.record": "Ficha",
  "chainMenu.push": "Enviar a las sucursales",
  "chainMenu.pushCount": "Enviar {count} artículos",
  "chainMenu.pushTitle": "Enviar {count} artículos",
  "chainMenu.pushHint":
    "Elija los locales a los que enviar. Un artículo que un local agregó por su cuenta nunca se toca, y los precios solo se envían entre sucursales que operan en la misma moneda.",
  "chainMenu.pushing": "Enviando…",
  "chainMenu.noTargets": "Esta cadena no tiene más locales que la oficina central.",
  "chainMenu.overwriteLocal": "Sobrescribir las ediciones propias de los locales",
  "chainMenu.overwriteLocalHint":
    "Desactivado de forma predeterminada. Un local que editó un artículo enviado conserva su versión, y el envío lo reporta como omitido.",
  "chainMenu.result": "Lo que recibió cada local",
  "chainMenu.resultLine": "{created} agregados, {updated} actualizados.",
  "chainMenu.resultSkipped": "{count} sin tocar: editados localmente.",
  "chainUsers.title": "Acceso a sucursales",
  "chainUsers.description":
    "En qué locales puede trabajar cada cuenta de administración. Las cuentas nuevas alcanzan toda la cadena hasta que se acoten aquí.",
  "chainUsers.name": "Persona",
  "chainUsers.username": "Usuario",
  "chainUsers.email": "Correo",
  "chainUsers.role": "Puesto",
  "chainUsers.record": "Ficha",
  "chainUsers.reach": "Alcanza",
  "chainUsers.stores": "Locales",
  "chainUsers.wholeChain": "Toda la cadena",
  "chainUsers.wholeChainHint": "Todos los locales, incluidos los que se agreguen después.",
  "chainUsers.namedStores": "Solo estos locales",
  "chainUsers.namedStoresHint":
    "Pueden entrar, cambiar entre ellos y hacer reportes de estos locales, y de ningún otro.",
  "chainUsers.storeCount": "{count} locales",
  "chainUsers.you": "Usted",
  "capability.configure_coupons": "Sus propios cupones",
  "capability.configure_discount_promos": "Sus propias promociones de descuento",
  "capability.configure_topup_tiers": "Sus propios paquetes de recarga",
  "capability.custom_topup_amounts": "Montos de recarga tecleados a mano",

  "settings.invoice.title": "Numeración de facturas",
  "settings.invoice.description":
    "El número impreso en una cuenta, y la identidad fiscal impresa encima.",
  "settings.invoice.numbering": "Numeración",
  "settings.invoice.enabled": "Imprimir un número de factura en los recibos",
  "settings.invoice.prefix": "Prefijo",
  "settings.invoice.dateType": "Reiniciar la secuencia",
  "settings.invoice.dateType.none": "Nunca: una sola secuencia continua",
  "settings.invoice.dateType.year": "Cada año",
  "settings.invoice.dateType.date": "Cada día",
  "settings.invoice.nextNumber": "Siguiente número",
  "settings.invoice.rangeHint": "Su mercado permite de {min} a {max}.",
  "settings.invoice.preview": "La siguiente factura dirá",
  "settings.invoice.continuity":
    "Los números se imprimen en una secuencia continua y solo pueden avanzar. Un cambio aplica a los pedidos nuevos; nada de lo ya impreso se vuelve a numerar.",
  "settings.invoice.taxIdentity": "Datos fiscales en el recibo",
  "settings.invoice.taxIdentityHint":
    "Se imprimen encima de los artículos cuando la plantilla del recibo está configurada para mostrarlos.",
  "settings.invoice.companyName": "Razón social",
  "settings.invoice.registrationNumber": "Número de registro fiscal",
  "settings.invoice.companyAddress": "Domicilio fiscal",
  "settings.invoice.deviceNumber": "Número de dispositivo registrado",
  "settings.invoice.deviceNumberHint":
    "Donde la autoridad fiscal registra la caja, su número debe figurar en cada factura. Déjelo vacío si no es su caso.",

  "tradingStatus.open": "Abierto",
  "tradingStatus.temporarily_closed": "Cerrado temporalmente",
  "tradingStatus.permanently_closed": "Cerrado definitivamente",

  "nav.items": "Artículos",
  "nav.categories": "Categorías",
  "nav.specifications": "Grupos de especificaciones",
  "nav.toppings": "Complementos",
  "nav.tags": "Etiquetas",
  "nav.tables": "Mesas",
  "nav.settings": "Configuración",
  "nav.storeProfile": "Perfil de la sucursal",
  "nav.tax": "Impuestos",
  "nav.serviceCharge": "Cargo por servicio",
  "nav.discountExclusions": "Exclusiones de descuento",
  "nav.paymentMethods": "Formas de pago",
  "nav.quickNotes": "Notas rápidas",

  "action.add": "Agregar",
  "action.view": "Ver",
  "action.edit": "Editar",
  "action.delete": "Eliminar",
  "action.save": "Guardar",
  "action.cancel": "Cancelar",
  "action.saving": "Guardando…",
  "action.select": "Elija una",
  "action.search": "Buscar",
  "action.clear": "Limpiar",
  "action.previous": "Anterior",
  "action.next": "Siguiente",
  "action.moveUp": "Mover arriba",
  "action.moveDown": "Mover abajo",
  "action.regenerate": "Regenerar",
  "action.close": "Cerrar",
  "action.confirmDelete": "Eliminar",

  "list.empty": "Todavía no hay nada aquí.",
  "list.noMatches": "Nada coincide con esa búsqueda.",
  "list.noFilterMatches": "Nada coincide con esos filtros.",
  "list.showing": "{from}–{to} de {total}",
  "list.noResults": "Sin resultados",
  "list.pagination": "Paginación",
  "list.page": "Página {page}",
  "list.morePages": "Más páginas",
  "list.perPage": "Filas por página",
  "list.perPageOption": "{count} por página",
  "list.sortAscending": "orden ascendente",
  "list.sortDescending": "orden descendente",
  "list.loadFailed": "No se pudo cargar esta lista.",
  "list.saveFailed": "No se pudo guardar eso.",

  "calendar.previousMonth": "Mes anterior",
  "calendar.nextMonth": "Mes siguiente",

  "dateRange.label": "Rango de fechas",
  "dateRange.apply": "Aplicar",
  "dateRange.today": "Hoy",
  "dateRange.yesterday": "Ayer",
  "dateRange.last7Days": "Últimos 7 días",
  "dateRange.last30Days": "Últimos 30 días",
  "dateRange.monthToDate": "Mes hasta hoy",
  "dateRange.lastMonth": "Mes anterior",
  "dateRange.yearToDate": "Año hasta hoy",
  "dateRange.lastYear": "Año anterior",

  "items.title": "Artículos",
  "items.description":
    "Todo lo que está en el menú, con su precio, sus modificadores y su enrutamiento a estación.",
  "items.searchPlaceholder": "Buscar por nombre o código",
  "items.new": "Nuevo artículo",
  "items.edit": "Editar artículo",
  "items.record": "Ficha",
  "items.money": "Dinero",
  "items.name": "Nombre",
  "items.code": "Código del artículo",
  "items.codeHint": "El SKU o PLU impreso en una comanda.",
  "items.price": "Precio",
  "items.priceHint": "En {currency}.",
  "items.category": "Categorías",
  "items.status": "Estado",
  "items.recipe": "De qué está hecho",
  "items.recipeHint":
    "Los insumos vinculados salen de existencias cuando se cobra la cuenta, no cuando se toma el pedido.",
  "items.soldOut": "Agotado",
  "items.autoSoldOut": "Que lo decidan las existencias",
  "items.autoSoldOutHint":
    "Se agota solo cuando se acaba un insumo de su receta, y regresa cuando llega existencia. Un artículo sin receta se queda como está.",
  "items.available": "Disponible",
  "items.madeToOrder": "Hecho al momento",
  "items.stations": "Estaciones",
  "items.stationsHint":
    "Donde se prepara. Puede ser más de un sitio, y cada uno recibe su propia comanda.",
  "items.stationsNone": "Sin estación",
  "items.stationPrinters": "Impresoras",
  "items.stationPrintersAll": "Todas las impresoras de esta estación",
  "items.packingFee": "Cargo por empaque",
  "items.kind": "Tipo de artículo",
  "items.kindHint":
    "Un platillo simple, un combo hecho de otros platillos, algo de una sola vez, o algo pedido con anticipación.",
  "items.kind.normal": "Platillo común",
  "items.kind.mealSet": "Combo",
  "items.kind.temporary": "De una sola vez",
  "items.kind.preOrder": "Pedido con anticipación",
  "items.pricingMethod": "Se vende por",
  "items.pricing.unit": "El platillo",
  "items.pricing.weight": "Peso",
  "items.wasPrice": "Precio anterior",
  "items.deliveryPrice": "Precio de entrega",
  "items.deliveryPriceHint":
    "Lo que cuesta este artículo en una plataforma de entrega. Déjelo vacío para venderlo al precio de la carta. Las plataformas cobran una comisión, por lo que la mayoría de las cartas se recargan aquí.",
  "items.wasPriceHint": "Se muestra tachado junto al precio. Déjelo vacío si no hay oferta.",
  "items.pricedAtTill": "Se le pone precio en la caja cada día",
  "items.mealSet": "Qué trae el combo",
  "items.mealSetHint":
    "Grupos de opciones. Cada opción es otro platillo de este menú, con el precio de lo que agrega.",
  "items.mealSetGroup": "Opción",
  "items.mealSetPicks": "Elegir",
  "items.mealSetPick": "Elija un platillo",
  "items.mealSetDelta": "Extra",
  "items.mealSetAddOption": "Agregar un platillo",
  "items.mealSetAddGroup": "Agregar una opción",
  "items.deleteTitle": "¿Eliminar este artículo?",
  "items.deleteBody":
    "Desaparece del menú. Los pedidos anteriores conservan su propio registro de lo que se vendió, así que nada de su historial cambia.",
  "items.allCategories": "Todas las categorías",
  "items.allStatuses": "Cualquier estado",
  "items.uncategorised": "Sin categoría",
  "items.nameInLocale": "Nombre en {locale}",
  "items.translationHint": "Los comensales que piden en {locale} ven esto en su lugar.",
  "items.modifiers": "Modificadores",
  "items.modifiersHint": "Lo que se le pregunta al cajero al tocar este plato.",
  "items.modifiersNone": "Ninguno",

  "categories.title": "Categorías",
  "categories.description": "Cómo se agrupa el menú, y el orden en que aparece.",
  "categories.new": "Nueva categoría",
  "categories.edit": "Editar categoría",
  "categories.record": "Ficha",
  "categories.name": "Nombre",
  "categories.itemCount": "Artículos",
  "categories.allStatuses": "Cualquier estado",
  "categories.deleteTitle": "¿Eliminar esta categoría?",
  "categories.deleteBody":
    "Una categoría con artículos dentro no se puede eliminar. Muévalos primero.",

  "modifiers.specifications": "Grupos de especificaciones",
  "modifiers.specificationsDescription":
    "Opciones que cambian lo que se vende: tamaño, caliente o frío.",
  "modifiers.toppings": "Complementos",
  "modifiers.toppingsDescription": "Extras con precio que un comensal puede agregar.",
  "modifiers.tags": "Etiquetas",
  "modifiers.tagsDescription":
    "Indicaciones para la estación. Pueden llevar precio si usted los cobra.",
  "modifiers.record": "Ficha",
  "modifiers.options": "Opciones",
  "modifiers.optionName": "Nombre",
  "modifiers.optionPrice": "Cambio de precio",
  "modifiers.optionStatus": "Estado",
  "modifiers.addOption": "Agregar una opción",
  "modifiers.required": "Hay que elegir una",
  "modifiers.selection": "Selección",
  "modifiers.single": "Solo una",
  "modifiers.multiple": "Las que sean",
  "modifiers.optionDefault": "Marcada por defecto",
  "modifiers.storeDefault": "Añadir a cada plato nuevo",
  "modifiers.usedBy": "Usado por",
  "modifiers.deleteTitle": "¿Eliminar este grupo?",
  "modifiers.deleteBody":
    "Los platos vinculados dejan de ofrecer sus opciones. Los pedidos anteriores conservan su propio registro de lo que se eligió, así que nada de su historial cambia.",

  "tables.title": "Mesas",
  "tables.description": "El plano del local, y el código QR que lleva cada mesa.",
  "tables.new": "Nueva mesa",
  "tables.edit": "Editar mesa",
  "tables.record": "Ficha",
  "tables.name": "Mesa",
  "tables.area": "Zona",
  "tables.capacity": "Comensales",
  "tables.qrCode": "Código QR",
  "tables.status": "Estado",
  "tables.tabList": "Lista de mesas",
  "tables.tabFloorPlan": "Plano del local",
  "tables.viewsLabel": "Vistas de mesas",
  "tables.searchPlaceholder": "Buscar mesas…",
  "tables.count": "{count} mesas",
  "tables.qrOrdering": "Pedido por QR",
  "tables.viewQr": "Ver QR",
  "tables.qrExplainer":
    "Los clientes pueden escanear el código QR para ver el menú y hacer pedidos.",
  "tables.qrUnavailable":
    "El pedido por QR todavía no tiene una dirección pública configurada, así que no hay nada que un celular pueda escanear.",
  "tables.qrFor": "Código QR de {table}",
  "tables.printQr": "Imprimir",
  "tables.deleteTitle": "¿Eliminar {name}?",
  "tables.deleteBody":
    "La mesa y su código QR desaparecen. Los pedidos anteriores conservan su registro de ella.",
  "tables.statusHint":
    "La ocupación la marca la caja conforme se sienta y se atiende a los clientes.",
  "tables.decreaseCovers": "Menos comensales",
  "tables.increaseCovers": "Más comensales",
  "tables.emptyTitle": "Todavía no hay mesas",
  "tables.emptyBody":
    "Cree su primera mesa para empezar a tomar pedidos y a generar códigos QR de mesa.",
  "tables.floorPlanTitle": "El plano del local todavía no está hecho",
  "tables.floorPlanBody": "Por ahora las mesas y sus zonas se administran desde la lista de mesas.",
  "tables.areas": "Zonas",
  "tables.newArea": "Nueva zona",
  "tables.defaultArea": "Zona predeterminada",
  "tables.regenerateQrTitle": "¿Darle a esta mesa un código QR nuevo?",
  "tables.regenerateQrBody":
    "Cualquier código impreso de esta mesa deja de funcionar de inmediato. Imprima y coloque el nuevo antes del siguiente servicio.",
  "tables.tempCode": "Códigos sin mesa",
  "tables.tempCodeDescription":
    "Números para pedidos tomados sin mesa: el mostrador, la barra, un repartidor.",
  "tables.tempCodeEnabled": "Usar códigos sin mesa",
  "tables.tempCodePrefix": "Prefijo",
  "tables.tempCodeFrom": "Desde",
  "tables.tempCodeTo": "Hasta",

  "settings.store.title": "Perfil de la sucursal",
  "settings.store.description": "Quién es, dónde está y cuándo termina su día.",
  "settings.store.name": "Nombre de la sucursal",
  "settings.store.address": "Dirección",
  "settings.store.phone": "Teléfono",
  "settings.store.timezone": "Zona horaria",
  "settings.store.businessDayCutoff": "El día de operación termina a las",
  "settings.store.businessDayCutoffHint":
    "Lo cobrado después de medianoche cuenta para el día anterior, hasta esta hora. El día de un restaurante termina cuando termina el servicio.",
  "settings.store.statisticsBasis": "Los informes cuentan una venta en",
  "settings.store.statisticsBasisHint":
    "En qué día registra cada informe una venta. Es distinto de la hora de arriba: esa decide con qué día se sella una cuenta, esta decide cuál de las dos fechas selladas lee un informe. Una sucursal puede mantener un cierre a las 3:00 y aun así cerrar sus libros por meses naturales.",
  "settings.store.statisticsBasisBusinessDay": "El día de operación, que termina a esa hora",
  "settings.store.statisticsBasisCalendarDay": "El día natural, de medianoche a medianoche",
  "settings.store.languages": "Idiomas",
  "settings.store.languagesHint":
    "Qué idiomas ofrecen las pantallas y los recibos de esta sucursal. Los recibos en jemer se imprimen como imagen, porque una impresora térmica no puede representar esa escritura desde una página de códigos.",
  "settings.store.currency": "Moneda",
  "settings.store.primaryCurrency": "Moneda principal",
  "settings.store.secondCurrency": "Segunda moneda",
  "settings.store.exchangeRate": "Tipo de cambio",
  "settings.store.exchangeRateHint":
    "Cuántos {second} por un {primary}. Cambiarlo no altera las cuentas ya cobradas.",
  "settings.store.rateHistory": "Historial del tipo de cambio",
  "settings.store.changedBy": "Cambiado por",
  "settings.store.effectiveFrom": "Desde",

  "settings.tax.title": "Impuestos",
  "settings.tax.description": "Cómo se calcula el impuesto de una cuenta.",
  "settings.tax.rules": "Reglas de impuesto",
  "settings.tax.unset": "Defina su tasa",
  "settings.tax.newRule": "Nueva regla",
  "settings.tax.editRule": "Editar regla",
  "settings.tax.code": "Código",
  "settings.tax.name": "Nombre",
  "settings.tax.rate": "Tasa",
  "settings.tax.rateHint": "Como porcentaje. Se conservan dos decimales exactos.",
  "settings.tax.inclusive": "Incluido en el precio",
  "settings.tax.inclusiveHint":
    "Los precios de su menú ya contienen este impuesto. Se muestra en la cuenta pero no se le suma.",
  "settings.tax.exclusiveHint": "Este impuesto se suma a la cuenta en la caja.",
  "settings.tax.sequence": "Orden",
  "settings.tax.appliesToCategories": "Solo estas categorías",
  "settings.tax.appliesToCategoriesHint": "Déjelo vacío para aplicar a todo.",
  "settings.tax.appliesToDiningStyles": "Solo estos tipos de servicio",
  "settings.tax.appliesToServiceCharge": "También grava el cargo por servicio",
  "settings.tax.appliesToTableFee": "También grava el cargo por mesa",
  "settings.tax.chargeTax": "Cobrar impuesto",
  "settings.tax.chargeTaxHint":
    "Desactivarlo suspende el cobro y conserva sus reglas. No es lo mismo que una tasa cero, que sí registra una línea de impuesto.",
  "settings.tax.label": "Cómo lo llama",
  "settings.tax.labelHint": "IVA, ISH, Sales Tax: lo que aparezca en sus recibos.",
  "settings.tax.deleteTitle": "¿Eliminar esta regla de impuesto?",
  "settings.tax.deleteBody":
    "Deja de aplicarse a las cuentas nuevas. Las cuentas ya cobradas conservan la tasa con la que se gravaron.",

  "settings.serviceCharge.title": "Cargo por servicio",
  "settings.serviceCharge.description": "Lo que agrega por servicio, y cuándo.",
  "settings.serviceCharge.newRate": "Nueva tasa",
  "settings.serviceCharge.deleteTitle": "¿Eliminar esta tasa?",
  "settings.serviceCharge.deleteBody":
    "Deja de añadirse a las cuentas nuevas. Las cuentas ya cobradas conservan el cargo por servicio que se les aplicó.",
  "settings.serviceCharge.rate": "Tasa",
  "settings.serviceCharge.default": "Predeterminada",
  "settings.serviceCharge.appliesTo": "Aplica a",
  "settings.serviceCharge.calculation": "Se calcula",
  "settings.serviceCharge.onSubtotal": "Sobre toda la cuenta",
  "settings.serviceCharge.perUnit": "Por artículo",
  "settings.serviceCharge.calculationHint":
    "Por artículo cobra cada unidad y redondea cada una, lo que puede diferir por unos centavos de cobrar la cuenta entera.",
  "settings.serviceCharge.rounding": "Redondeo",
  "settings.serviceCharge.tableFee": "Cargo por mesa",
  "settings.serviceCharge.tableFeeEnabled": "Cobrar un cargo por mesa",
  "settings.serviceCharge.tableFeeBasis": "Se cobra",
  "settings.serviceCharge.perHead": "Por comensal",
  "settings.serviceCharge.perTable": "Por mesa",
  "settings.serviceCharge.amount": "Monto",
  "settings.tip.title": "Propinas",
  "settings.tip.description":
    "La propina es dinero del comensal que pasa al personal. El cargo por servicio es suyo. Son distintos ante la ley y usted puede usar ambos.",
  "settings.tip.enabled": "Ofrecer propina en la caja",
  "settings.tip.suggested": "Porcentajes sugeridos",
  "settings.tip.suggestedHint": "Hasta cuatro, separados por comas. 18, 20, 22 es lo habitual.",
  "settings.tip.basis": "Sugerir un porcentaje del",
  "settings.tip.basisHint":
    "Las terminales de tarjeta suelen sugerir sobre el total. La costumbre antigua es sobre el subtotal.",
  "settings.tip.basisTotal": "El total, con impuesto",
  "settings.tip.basisSubtotal": "El subtotal, antes de impuesto",

  "settings.discountExclusions.title": "Exclusiones de descuento",
  "settings.discountExclusions.description":
    "Artículos que un descuento a toda la cuenta nunca toca.",
  "settings.discountExclusions.excluded": "Excluidos",
  "settings.discountExclusions.selected": "{count} excluidos",

  "settings.paymentMethods.title": "Formas de pago",
  "settings.paymentMethods.description": "Lo que ofrece la caja, y en qué orden.",
  "settings.paymentMethods.enabled": "Se ofrece",
  "settings.paymentMethods.provider": "Proveedor",
  "settings.paymentMethods.noProvider": "Ninguno",
  "settings.paymentMethods.add": "Agregar un método de pago",
  "settings.paymentMethods.newName": "Cómo debe llamarlo la caja",
  "settings.paymentMethods.builtIn": "Del sistema",
  "settings.paymentMethods.builtInHint":
    "Los métodos del sistema se pueden renombrar o desactivar, no eliminar.",
  "settings.paymentMethods.nameHint":
    "Renombre cualquier método: la caja y los reportes lo siguen.",
  "settings.paymentMethods.deleteConfirm":
    "¿Eliminar este método de pago? Los pedidos anteriores conservan el nombre con el que se cobraron.",

  "settings.quickNotes.title": "Notas rápidas",
  "settings.quickNotes.description": "Atajos que un cajero toca en vez de teclear.",
  "settings.quickNotes.new": "Nueva nota",
  "settings.quickNotes.record": "Ficha",
  "settings.quickNotes.deleteTitle": "¿Eliminar esta nota?",
  "settings.quickNotes.deleteBody":
    "Los cajeros dejan de verla. Los pedidos que ya la llevan conservan el texto.",

  "diningStyle.dine_in": "Para comer aquí",
  "diningStyle.take_away": "Para llevar",
  "diningStyle.pre_order": "Pedido anticipado",
  "diningStyle.delivery": "Reparto",

  "status.enabled": "Activo",
  "status.disabled": "Oculto",
  "tableStatus.free": "Libre",
  "tableStatus.seated": "Sentados",
  "tableStatus.ordered": "Con pedido",
  "tableStatus.bill_requested": "Pidió la cuenta",

  "rounding.half_up": "Medio hacia arriba",
  "rounding.half_even": "Medio al par",
  "rounding.half_down": "Medio hacia abajo",
  "rounding.up": "Siempre hacia arriba",
  "rounding.down": "Siempre hacia abajo",
  "rounding.ceiling": "Hacia arriba",
  "rounding.floor": "Hacia abajo",

  "inventory.supplies.title": "Insumos",
  "inventory.supplies.description": "Lo que las estaciones compran, guardan y consumen.",
  "inventory.supplies.new": "Nuevo insumo",
  "inventory.supplies.deleteTitle": "¿Eliminar este insumo?",
  "inventory.supplies.deleteBody":
    "Un insumo que todavía tiene existencias no se puede eliminar: regístralas como merma " +
    "primero. Sus lotes y todos sus movimientos siguen donde están.",
  "inventory.number": "Número",
  "inventory.record": "Ficha",
  "inventory.name": "Nombre",
  "inventory.otherName": "También conocido como",
  "inventory.category": "Categoría",
  "inventory.brand": "Marca",
  "inventory.barcode": "Código de barras",
  "inventory.barcodeHint": "Tecléelo o escanéelo. Un insumo por código de barras.",
  "inventory.stockUnit": "Unidad de existencias",
  "inventory.stockUnitHint": "En qué se cuenta. No se puede cambiar una vez que hay existencias.",
  "inventory.purchaseUnit": "Unidad de compra",
  "inventory.salesUnit": "Unidad de venta",
  "inventory.minimumStock": "Avisar por debajo de",
  "inventory.inStock": "En existencia",
  "inventory.belowMinimum": "Bajo",
  "inventory.suppliers.title": "Proveedores",
  "inventory.suppliers.description": "A quién le compra, y cuánto cobra.",
  "inventory.suppliers.new": "Nuevo proveedor",
  "inventory.contactPerson": "Contacto",
  "inventory.contactNumber": "Teléfono",
  "inventory.address": "Dirección",
  "inventory.nickname": "Nombre corto",
  "inventory.supplierPrices": "Suministros",
  "inventory.warehouses.title": "Almacenes",
  "inventory.warehouses.description": "Dónde está la existencia, y qué lote sale primero.",
  "inventory.warehouses.new": "Nuevo almacén",
  "inventory.isDefault": "Descontar las ventas de aquí",
  "inventory.valuationPolicy": "Qué lote sale primero",
  "inventory.valuation.fifo": "El más viejo primero (PEPS)",
  "inventory.valuation.lifo": "El más nuevo primero (UEPS)",
  "inventory.valuationHint":
    "Esto decide cuánto cuesta una salida, no el orden en que se listan las cosas.",
  "inventory.units.title": "Unidades",
  "inventory.units.description":
    "Cómo se miden los insumos, y cómo una medida se convierte en otra.",
  "inventory.units.new": "Nueva unidad",
  "inventory.dimension": "Mide",
  "inventory.dimension.mass": "Peso",
  "inventory.dimension.volume": "Volumen",
  "inventory.dimension.count": "Piezas",
  "inventory.seeded": "De fábrica",
  "inventory.conversions": "Conversiones",
  "inventory.conversions.new": "Nueva conversión",
  "inventory.conversionFrom": "Uno de",
  "inventory.conversionTo": "hace",
  "inventory.factor": "Cuántos",
  "inventory.appliesTo": "Aplica a",
  "inventory.everySupply": "Todos los insumos",
  "inventory.categories.title": "Categorías de insumos",
  "inventory.categories.description": "Dos niveles: una categoría, y las subcategorías debajo.",
  "inventory.categories.new": "Nueva categoría",
  "inventory.categories.deleteTitle": "¿Eliminar esta categoría?",
  "inventory.categories.deleteBody":
    "Una categoría con insumos o subcategorías no se puede eliminar. Muévelos primero.",
  "inventory.parent": "Está debajo de",
  "inventory.topLevel": "Nada: es de primer nivel",
  "inventory.subCategories": "Subcategorías",
  "inventory.supplyCount": "Insumos",
  "inventory.brands.title": "Marcas",
  "inventory.brands.description": "De quién es el producto, donde eso importa.",
  "inventory.brands.new": "Nueva marca",
  "inventory.brands.deleteTitle": "¿Eliminar esta marca?",
  "inventory.brands.deleteBody":
    "Una marca que sigue en un insumo no se puede eliminar. Quítala de esos insumos primero.",
  "inventory.description": "Notas",

  "nav.stock": "Existencias",
  "nav.purchases": "Compras",
  "nav.inbound": "Entradas",
  "nav.outbound": "Salidas",
  "nav.stockCounts": "Conteos de existencias",
  "stock.title": "Existencias",
  "stock.description": "Qué hay a la mano, cuánto vale y qué se está acabando.",
  "stock.warehouse": "Almacén",
  "stock.quantity": "A la mano",
  "stock.value": "Valor",
  "stock.onlyLow": "Solo lo que se está acabando",
  "stock.batches": "Lotes",
  "stock.usedIn": "Se usa en",
  "stock.batchNo": "Lote",
  "stock.received": "Recibido",
  "stock.remaining": "Restante",
  "stock.unitCost": "Costo unitario",
  "stock.receivedAt": "Ingresado",
  "stock.expiresAt": "Consumir antes de",
  "stock.drawOrder": "Se toma en este orden",
  "stock.drawOrderReverse": "Se toma en orden inverso a este",
  "purchases.title": "Compras",
  "purchases.description": "Lo que se ha pedido, y lo que ha llegado.",
  "purchases.new": "Nueva compra",
  "purchases.type": "Comprado a",
  "purchases.type.supplier": "Un proveedor",
  "purchases.type.market": "El mercado",
  "purchases.type.head_office": "Oficina central",
  "purchases.supplier": "Proveedor",
  "purchases.total": "Total",
  "purchases.notifyInbound": "Enviar al almacén",
  "purchases.receiveNow": "Ingresar ahora",
  "purchases.void": "Anular",
  "purchases.voidReason": "Motivo",
  "inbound.title": "Entradas",
  "inbound.description": "Entregas esperando revisión, y lo que ya se ingresó.",
  "inbound.new": "Nueva entrada",
  "inbound.type": "Motivo",
  "inbound.type.supplier": "De un proveedor",
  "inbound.type.market": "Del mercado",
  "inbound.type.item_return": "Devuelto",
  "inbound.type.overstock": "Encontrado en existencia",
  "inbound.type.transfer": "Traspaso recibido",
  "inbound.approve": "Ingresarlo",
  "inbound.reject": "Rechazar",
  "inbound.rejectReason": "Motivo",
  "inbound.batchNo": "Lote",
  "inbound.unitCost": "Costo unitario",
  "inbound.freeQuantity": "Gratis",
  "outbound.title": "Salidas",
  "outbound.description": "Qué salió, por qué, y cuánto costó.",
  "outbound.new": "Nueva salida",
  "outbound.type": "Motivo",
  "outbound.type.loss": "Merma",
  "outbound.type.sales": "Vendido tal cual",
  "outbound.type.transfer": "Traspaso",
  "outbound.type.item_sales": "Vendido como platillo",
  "outbound.type.shortage": "Faltante",
  "outbound.destination": "Al almacén",
  "outbound.cost": "Costo",
  "outbound.shortfall": "Faltan",
  "outbound.customer": "Cliente",
  "outbound.salesAmount": "Vendido en",
  "counts.title": "Conteos de existencias",
  "counts.description": "Lo que dice el anaquel, contra lo que dicen los libros.",
  "counts.new": "Nuevo conteo",
  "counts.scope": "Contar",
  "counts.scope.all": "Todo",
  "counts.scope.picked": "Solo lo que yo elija",
  "counts.counter": "Contado por",
  "counts.expected": "Los libros dicen",
  "counts.counted": "El anaquel dice",
  "counts.difference": "Diferencia",
  "counts.record": "Registrar el conteo",
  "counts.adjust": "Aplicar la diferencia",
  "counts.adjustHint":
    "Esto mueve existencias sin una entrega detrás, así que queda registrado a su nombre.",
  "document.number": "Número",
  "document.status": "Estado",
  "document.date": "Levantado",
  "document.lines": "Renglones",
  "document.addLine": "Agregar un renglón",
  "document.quantity": "Cantidad",
  "document.unitPrice": "Precio unitario",
  "document.remarks": "Notas",
  "status.purchasing": "Comprando",
  "status.head_office_reviewing": "Con la oficina central",
  "status.head_office_rejected": "La oficina central dijo que no",
  "status.pending_inbound": "Esperando ingreso",
  "status.ended": "Terminado",
  "status.voided": "Anulado",
  "status.draft": "Borrador",
  "status.waiting_review": "Esperando revisión",
  "status.approving": "En revisión",
  "status.received": "Ingresado",
  "status.rejected": "Rechazado",
  "status.issued": "Salió",
  "status.transferring": "En tránsito",
  "status.blocked": "No hay existencias suficientes",
  "status.counting": "Contando",
  "status.adjusted": "Aplicado",

  "nav.ordersSection": "Pedidos",
  "nav.orderList": "Todos los pedidos",
  "nav.reports": "Reportes",
  "nav.costs": "Gastos de la sucursal",
  "nav.deliveryProviders": "Plataformas de reparto",
  "nav.billing": "Suscripción",
  "nav.campaigns": "Campañas",
  "billing.title": "Su suscripción",
  "billing.description": "El plan que tiene, qué incluye y qué ha pagado.",
  "billing.plan": "Plan",
  "billing.perMonth": "{amount} al mes",
  "billing.permanent": "Este plan no vence.",
  "billing.expires": "Vigente hasta el {date}: quedan {days} días.",
  "billing.lapsed": "Venció el {date}.",
  "billing.allowances": "Lo que permite",
  "billing.unmetered": "{used}: sin límite",
  "billing.renew": "Extender",
  "billing.renewHint":
    "Extiende el plan que tiene. Cambiar de plan requiere un contrato nuevo: hable con nosotros.",
  "billing.months": "Meses",
  "billing.cycle": "Se factura",
  "billing.monthly": "Cada mes",
  "billing.yearly": "Cada año",
  "billing.payments": "Pagos",
  "billing.noPayments": "Todavía no se ha facturado nada.",
  "subscriptionStatus.normal": "Activa",
  "subscriptionStatus.expired": "Vencida",
  "subscriptionStatus.invalidated": "Suspendida",
  "subscriptionStatus.cancelled": "Cancelada",
  "subscriptionStatus.trialing": "De prueba",
  "quota.item": "Artículos",
  "quota.employee": "Personal",
  "quota.table": "Mesas",
  "quota.customer": "Clientes",
  "quota.supplier": "Proveedores",
  "quota.store": "Locales",
  "campaigns.title": "Campañas",
  "campaigns.description":
    "Cada enésimo artículo con descuento. Se calcula en la cuenta, no se teclea a mano.",
  "campaigns.add": "Agregar una campaña",
  "campaigns.name": "Nombre",
  "campaigns.kind": "Oferta",
  "campaigns.kind.half_price_second": "El segundo artículo a mitad de precio",
  "campaigns.kind.buy_one_free_one": "Compre uno y llévese otro gratis",
  "campaigns.kind.nth_item": "Enésimo artículo con descuento",
  "campaigns.nthItem": "Cuál artículo",
  "campaigns.discount": "Cuánto de descuento",
  "campaigns.discountHint": "Un porcentaje. 100 significa gratis.",
  "campaigns.consecutive": "Repetir a lo largo del carrito",
  "campaigns.consecutiveHint":
    "Activado: cuatro artículos dan dos en oferta. Desactivado: se aplica una vez por cuenta.",
  "campaigns.active": "En marcha",
  "campaigns.reads": "Cada {nth} artículo, {percent} % de descuento",
  "campaigns.deleteTitle": "¿Eliminar esta campaña?",
  "campaigns.deleteBody":
    "La campaña desaparece para siempre, no se pausa: desactivarla detiene la oferta y la conserva para después. Las cuentas ya calculadas con ella no cambian.",
  "campaigns.record": "Ficha",
  "campaigns.label": "Etiqueta en la cuenta",
  "campaigns.items": "Artículos que cubre",
  "campaigns.itemsAll": "Toda la carta",
  "campaigns.window": "Cuándo se aplica",
  "campaigns.validFrom": "Desde",
  "campaigns.validTo": "Hasta",
  "nav.exports": "Exportaciones",
  "nav.chainOrders": "Pedidos de la cadena",
  "nav.insights": "Indicadores del negocio",
  "nav.chainReports": "Reportes de la cadena",
  "orders.title": "Pedidos",
  "orders.description": "Cada cuenta, qué llevaba y qué pasó con ella.",
  "orders.number": "Pedido",
  "orders.table": "Mesa",
  "orders.covers": "Comensales",
  "orders.total": "Total",
  "orders.paid": "Pagado",
  "orders.status": "Estado",
  "orders.date": "Tomado",
  "orders.diningStyle": "Servicio",
  "orders.employee": "Registrado por",
  "orders.refund": "Reembolsar",
  "orders.tip": "Propina",
  "orders.adjustTip": "Ajustar propina",
  "orders.adjustTipTitle": "Ajustar la propina de este cobro",
  "orders.adjustTipHint":
    "La propina como debe quedar, no la diferencia. Solo hasta que se cuente este día de operación.",
  "orders.tipAdjusted": "Propina ajustada.",
  "orders.refundTitle": "Reembolsar esta cuenta",
  "orders.refundAmount": "Monto",
  "orders.refundAmountHint": "Déjelo vacío para reembolsar todo lo que sigue cobrado.",
  "orders.refundReason": "Motivo",
  "orders.refundable": "Todavía reembolsable",
  "orders.refunded": "Reembolsado.",
  "orders.reverse": "Revertir",
  "orders.reverseTitle": "Revertir este cobro",
  "orders.reverseWarning":
    "La cuenta vuelve a quedar abierta, y se devuelve el dinero cobrado. Su mesa queda ocupada de nuevo.",
  "orders.reversed": "Cobro revertido.",
  "orders.items": "Artículos",
  "orders.taxLines": "Impuestos",
  "orders.payments": "Pago",
  "orders.history": "Historial",
  "orders.reprint": "Reimprimir el recibo",
  "orders.reprinted": "En cola para imprimirse.",
  "orders.allStatuses": "Cualquier estado",
  "orders.allStyles": "Cualquier servicio",
  "receipt.taxIncluded": "incluido",
  "orders.inclusiveNote": "Ya incluido en el precio",
  "payStatus.unpaid": "Abierta",
  "payStatus.partly_paid": "Pagada en parte",
  "payStatus.paid": "Pagada",
  "payStatus.credit": "A cuenta",
  "payStatus.refunded": "Reembolsada",
  "payStatus.cancelled": "Cancelada",
  "orderItem.ordered": "Pedido",
  "orderItem.fired": "Con la estación",
  "orderItem.served": "Servido",
  "orderItem.returned": "Devuelto",
  "orderItem.voided": "Anulado",
  "event.created": "Pedido tomado",
  "event.accepted": "Aceptado",
  "event.item_added": "Se pidió más",
  "event.item_returned": "Artículo devuelto",
  "event.item_voided": "Artículo anulado",
  "event.item_fired": "Enviado a la estación",
  "event.table_changed": "Cambio de mesa",
  "event.covers_changed": "Cambio de comensales",
  "event.held": "En espera",
  "event.resumed": "Retomada",
  "event.merged": "Unida",
  "event.split": "Dividida",
  "event.discounted": "Con descuento",
  "event.tax_exempted": "Exenta de impuesto",
  "event.tax_reinstated": "Impuesto restablecido",
  "event.tip_adjusted": "Propina ajustada",
  "event.paid": "Pagada",
  "event.refunded": "Reembolsada",
  "event.reversed": "Cobro revertido",
  "event.cancelled": "Cancelada",
  "event.member_attached": "Cliente identificado",
  "event.points_earned": "Puntos ganados",
  "event.supplies_deducted": "Existencias descontadas",
  "creditStatus.open": "Abierta",
  "creditStatus.settled": "Saldada",
  "creditStatus.written_off": "Incobrable",
  "insights.title": "Indicadores del negocio",
  "insights.description": "Cómo va la cadena, y qué se ve mal.",
  "insights.overview": "Resumen",
  "insights.trend": "Tendencia",
  "insights.priceDistribution": "Distribución de precios",
  "insights.anomalies": "Qué se ve mal",
  "insights.rules": "Umbrales",
  "insights.storeCount": "Sucursales",
  "insights.allStores": "Todas las sucursales",
  "insights.comparedWith": "contra {from} a {to}",
  "insights.metric": "Métrica",
  "insights.metric.net": "Ventas netas",
  "insights.metric.orders": "Pedidos",
  "insights.metric.covers": "Comensales",
  "insights.metric.discount": "Descuentos",
  "insights.metric.perCover": "Gasto por comensal",
  "insights.figure.net": "Ventas netas",
  "insights.figure.gross": "Ventas brutas",
  "insights.figure.discount": "Descuentos",
  "insights.figure.refunded": "Reembolsos",
  "insights.figure.orders": "Pedidos",
  "insights.figure.covers": "Comensales",
  "insights.column.store": "Sucursal",
  "insights.column.item": "Artículo",
  "insights.column.lowest": "Más barato en",
  "insights.column.highest": "Más caro en",
  "insights.column.spread": "Diferencia",
  "insights.column.median": "Mediana",
  "insights.column.stores": "Sucursales",
  "insights.column.perCover": "Por comensal",
  "insights.column.rule": "Regla",
  "insights.priceScope":
    "Precios de carta actuales en todas las sucursales, sea cual sea la sucursal elegida arriba. El rango de fechas no se aplica, porque no hay histórico de precios con el que leer un periodo pasado.",
  "insights.noSpread": "Ningún artículo tiene precio distinto entre sucursales.",
  "insights.priceTruncated":
    "Mostrando {shown} de {total} artículos con la mayor diferencia de precio.",
  "insights.column.reading": "Lectura",
  "insights.column.threshold": "Umbral",
  "insights.column.evidence": "Evidencia",
  "insights.showEvidence": "Muéstrame",
  "insights.rule.discountRate": "Tasa de descuento",
  "insights.rule.voidRate": "Tasa de anulaciones",
  "insights.rule.refundRate": "Tasa de reembolsos",
  "insights.rule.coverSpend": "Gasto por comensal",
  "insights.direction.above": "por encima de",
  "insights.direction.below": "por debajo de la mediana de la cadena",
  "insights.clear": "Nada cruzó un umbral en este periodo.",
  "insights.enabled": "Vigilado",
  "insights.saved": "Guardado.",
  "chainOrders.title": "Pedidos de la cadena",
  "chainOrders.description": "Cada pedido tomado en toda la cadena.",
  "chainOrders.store": "Sucursal",
  "chainOrders.record": "Ficha",
  "nav.members": "Clientes",
  "nav.memberList": "Lista de clientes",
  "nav.memberBalance": "Saldo en la sucursal",
  "nav.loyalty": "Lealtad",
  "members.title": "Clientes",
  "members.description": "Quién regresa, qué tiene y qué ha ganado.",
  "members.number": "N.º de cliente",
  "members.name": "Nombre",
  "members.phone": "Teléfono",
  "members.balance": "Saldo",
  "members.points": "Puntos",
  "members.tier": "Nivel",
  "members.joined": "Se inscribió",
  "members.enrol": "Inscribir a un cliente",
  "members.classification": "Cliente de",
  "members.anyClassification": "Cualquier parte de la cadena",
  "members.classification.our_store": "Esta sucursal",
  "members.classification.chain_store": "Otra sucursal",
  "members.classification.non_chain": "Fuera de la cadena",
  "members.record": "Ficha",
  "members.registeredAt": "Se registró en",
  "members.sharedWith": "Saldo compartido con",
  "members.noStoredValue": "No está en este plan",
  "members.topUp": "Recargar",
  "members.adjust": "Corregir el saldo",
  "members.setPin": "Asignar el PIN",
  "members.pin": "PIN",
  "members.pinSet": "Asignado",
  "members.pinUnset": "Sin asignar",
  "members.amount": "Monto",
  "members.topUpHint": "El bono del paquete que corresponda se agrega automáticamente.",
  "members.adjustPoints": "Ajustar puntos",
  "members.balanceHistory": "Historial de saldo",
  "members.pointsHistory": "Historial de puntos",
  "members.noMovements": "Todavía no se ha movido dinero.",
  "members.noPoints": "Todavía no hay puntos.",
  "members.movement": "Movimiento",
  "members.balanceAfter": "Saldo después",
  "members.whereStore": "Dónde",
  "members.movement.topup": "Recarga",
  "members.movement.spend": "Gastado",
  "members.movement.refund": "Reembolsado",
  "members.movement.adjust": "Corrección",
  "members.points.earn": "Ganados",
  "members.points.redeem": "Canjeados",
  "members.points.adjust": "Corrección",
  "members.points.expire": "Vencidos",
  "balance.title": "Saldo en la sucursal",
  "balance.description": "Lo que les debe a sus clientes, y qué sucursal lo cobró.",
  "balance.liability": "Adeudado a los clientes",
  "balance.holders": "Clientes con saldo",
  "balance.byStore": "Por sucursal",
  "balance.toppedUp": "Cobrado",
  "balance.spent": "Servido",
  "balance.held": "Todavía en poder",
  "balance.share": "Parte",
  "loyalty.title": "Lealtad",
  "loyalty.description": "Bonos de recarga, y lo que gana una cuenta.",
  "loyalty.tiers": "Paquetes de recarga",
  "loyalty.noTiers": "Todavía no hay paquetes: una recarga no gana bono.",
  "loyalty.tierReads": "Pague {pay}, reciba {get}",
  "loyalty.pay": "Paga",
  "loyalty.bonus": "Bono",
  "loyalty.points": "Puntos",
  "loyalty.pointsEnabled": "Otorgar puntos en las cuentas cobradas",
  "loyalty.perPoint": "Gasto por punto",
  "loyalty.pointWorth": "Un punto vale",
  "loyalty.minimumRedeemable": "Mínimo de puntos canjeables",
  "action.yes": "Sí",
  "action.no": "No",
  "weekday.0": "Dom",
  "weekday.1": "Lun",
  "weekday.2": "Mar",
  "weekday.3": "Mié",
  "weekday.4": "Jue",
  "weekday.5": "Vie",
  "weekday.6": "Sáb",
  "nav.marketing": "Marketing",
  "nav.coupons": "Cupones",
  "nav.promos": "Promociones",
  "coupons.title": "Cupones",
  "coupons.description": "Qué regala, a quién, y cuánto le cuesta.",
  "coupons.name": "Cupón",
  "coupons.worth": "Vale",
  "coupons.minSpend": "Consumo mínimo",
  "coupons.issued": "Emitidos",
  "coupons.used": "Canjeados",
  "coupons.given": "Regalados",
  "coupons.status": "Estado",
  "coupons.live": "Activo",
  "coupons.off": "Apagado",
  "coupons.design": "Diseñar un cupón",
  "coupons.copy": "Copiar",
  "coupons.send": "Enviar",
  "coupons.switchOn": "Encender",
  "coupons.switchOff": "Apagar",
  "coupons.kind": "Tipo",
  "coupons.kind.amount": "Un monto de descuento",
  "coupons.kind.percent": "Un porcentaje de descuento",
  "coupons.amountOff": "Monto de descuento",
  "coupons.percentOff": "Porcentaje de descuento, en puntos base",
  "coupons.maxDiscount": "Como máximo",
  "coupons.maxDiscountHint": "Déjelo vacío para no poner tope.",
  "coupons.record": "Ficha",
  "coupons.limits": "Cuántos se reparten",
  "coupons.issueLimit": "En total",
  "coupons.perMemberLimit": "Por miembro",
  "coupons.window": "Cuándo se aplica",
  "coupons.validFrom": "Desde",
  "coupons.validTo": "Hasta",
  "coupons.performance": "Lo que ha hecho",
  "promos.title": "Promociones",
  "promos.description": "Gaste y ahorre, en el orden en que se consideran.",
  "promos.name": "Promoción",
  "promos.offer": "Oferta",
  "promos.when": "Cuándo",
  "promos.stacks": "Se acumula",
  "promos.add": "Agregar una promoción",
  "promos.reads": "Gaste {threshold}, ahorre {reduction}",
  "promos.threshold": "Gaste al menos",
  "promos.reduction": "Descontar",
  "promos.days": "Días",
  "promos.everyDay": "Todos los días",
  "promos.deleteTitle": "¿Eliminar esta promoción?",
  "promos.deleteBody":
    "La promoción desaparece para siempre, no se pausa: desactivarla detiene la oferta y la conserva para después. Las cuentas ya calculadas con ella no cambian.",
  "promos.record": "Ficha",
  "promos.order": "Se considera en",
  "promos.hours": "Horario",
  "promos.allDay": "Todo el día",
  "promos.validFrom": "Desde",
  "promos.validTo": "Hasta",
  "pos.offline.ordering": "Tomar pedidos",
  "pos.offline.payment": "Efectivo y pago sin conexión",
  "pos.offline.printing": "Impresión de recibos",
  "pos.offline.noIncomingOrders": "Los pedidos de otros terminales no llegarán a esta caja",
  "pos.offline.noQrOrders": "Los pedidos por QR hechos mientras está sin conexión no aparecerán",
  "pos.offline.noOnlinePayment": "El pago en línea y el saldo del cliente no están disponibles",
  "pos.offline.noReports": "Los reportes y las cifras de existencias estarán desactualizados",
  "pos.offline.willSync": "Todo lo tomado sin conexión se enviará ahora; puede tardar un momento",
  "pos.sync.syncing": "Los datos se están sincronizando…",
  "pos.sync.waiting": "{count} esperando envío",
  "pos.sync.needsAttention": "{count} no se pudieron enviar",
  "pos.failures.title": "No se pudieron enviar",
  "pos.failures.intro":
    "El servidor los rechazó y los seguirá rechazando. Cada uno necesita una decisión, sobre todo un pago, que es dinero de alguien.",
  "pos.failures.none": "Nada está esperando por usted.",
  "pos.failures.money": "Pago",
  "pos.failures.change": "Cambio",
  "pos.failures.queuedAt": "Tomado {at}",
  "pos.failures.dismiss": "Ya me hice cargo de esto",
  "pos.failures.close": "Cerrar",

  "pos.bill.startTitle": "Abrir una cuenta",
  "pos.bill.startBody":
    "Elija una mesa, o abra una cuenta para llevar, antes de agregar artículos.",
  "pos.bill.chooseTable": "Elija una mesa",
  "pos.bill.heldLabel": "{name}: en espera",
  "pos.bill.newTakeAway": "Para llevar",
  "pos.bill.itemsBlocked": "Abra una cuenta antes de agregar artículos",
  "pos.bill.open": "Cuentas abiertas",
  "pos.bill.opening": "Abriendo…",
  "pos.bill.notSent": "Todavía sin enviar",
  "pos.bill.failed": "No se pudo abrir esta cuenta",
  "pos.bill.clearTable": "Liberar mesa",
  "pos.bill.clearTableConfirm": "¿Liberar {table}? No se ha pedido nada en ella.",
  "pos.bill.clearTableKeep": "Dejarla así",
  "pos.bill.discardBill": "Descartar cuenta",
  "pos.bill.discardBillConfirm": "¿Descartar esta cuenta? No se ha pedido nada en ella.",
  "pos.bill.discard": "Descartar",
  "pos.bill.release": "Liberar",
  "pos.bill.occupied": "Ocupada",
  "pos.bill.openThisBill": "Abrir esta cuenta",
  "pos.bill.resumeOffline": "Esta cuenta se abrió en otro terminal. Conéctese para abrirla aquí.",
  "pos.cart.table": "Mesa",
  "pos.cart.takeAway": "Para llevar",
  "pos.cart.covers": "{count} comensales",
  "pos.cart.editCovers": "Mantenga presionado para cambiar los comensales",
  "pos.cart.empty": "Todavía no hay nada en esta cuenta.",
  "pos.cart.ordered": "Pedido ({count})",
  "pos.cart.latestOrder": "Último pedido ({count})",
  "pos.cart.subtotal": "Subtotal",
  "pos.cart.discount": "Descuento",
  "pos.cart.addDiscount": "Agregar",
  "pos.cart.service": "Servicio",
  "pos.cart.total": "Total",
  "pos.cart.taxFree": "Sin impuesto",
  "pos.cart.taxFreeApply": "Aplicar",
  "pos.cart.taxFreeRemove": "Quitar",
  "pos.cart.hold": "En espera",
  "pos.cart.resume": "Retomar",
  "pos.cart.sendToStation": "Enviar a la estación",
  "pos.cart.settle": "Cobrar",
  "pos.sync.busy": "Sincronizando…",
  "pos.sync.failed": "{count} fallaron",
  "pos.sync.queued": "{count} en cola",
  "pos.menu.all": "Todo",
  "pos.menu.loading": "Cargando el menú…",
  "pos.menu.soldOut": "Agotado",
  "pos.menu.searchPlaceholder": "Buscar por nombre o código",
  "pos.menu.result": "{count} resultado",
  "pos.menu.results": "{count} resultados",
  "pos.menu.inCart": "{count} en el carrito",
  "pos.item.quantity": "Cantidad",
  "pos.item.note": "Nota para la estación",
  "pos.item.notePlaceholder": "Sin hielo, bien picante…",
  "pos.item.chooseOne": "elija una",
  "pos.item.chooseAny": "elija al menos una",
  "pos.item.optional": "opcional",
  "pos.item.cancel": "Cancelar",
  "pos.item.choose": "Elija {group}",
  "pos.item.add": "Agregar {total}",
  "pos.mode.online": "En línea",
  "pos.mode.offline": "Sin conexión",
  "pos.mode.goOffline": "¿Pasar a sin conexión?",
  "pos.mode.comeOnline": "¿Volver a estar en línea?",
  "pos.mode.stillAble": "Esta caja todavía podrá:",
  "pos.mode.butNot": "Pero no:",
  "pos.mode.allSent": "Ya se envió todo.",
  "pos.mode.stayOnline": "Seguir en línea",
  "pos.mode.stayOffline": "Seguir sin conexión",
  "pos.mode.switchToOnline": "Ponerse en línea",
  "pos.mode.switchToOffline": "Pasar a sin conexión",
  "pos.identify.heading": "Escanee para iniciar sesión",
  "pos.identify.instruction":
    "Apunte la cámara de su teléfono a este código y confirme en la app del personal.",
  "pos.identify.codeLabel": "O escriba este código:",
  "pos.identify.denied": "Ese inicio de sesión fue rechazado. Escanee de nuevo.",
  "pos.identify.notConfigured":
    "Esta caja no tiene una dirección de app del personal configurada, así que nadie puede iniciar sesión aquí. Consulte a quien configuró la caja.",
  "pos.identify.unavailable":
    "Esta caja no puede mostrar un código de inicio de sesión ahora mismo. Compruebe la conexión.",
  "pos.identify.retry": "Reintentar",
  "pos.identify.choice.checkIn": "Fichar entrada",
  "pos.identify.choice.take": "Tomar esta caja",
  "pos.identify.choice.checkInAndTake": "Fichar entrada y tomar esta caja",
  "pos.identify.choice.checkOut": "Fichar salida",
  "pos.identify.choice.cancel": "Cancelar",
  "pos.identify.choice.jobHeading": "¿Qué puesto?",
  "pos.identify.choice.noJobs": "Esta tienda aún no tiene puestos configurados.",
  "pos.identify.choice.elsewhere": "Ya ha fichado la entrada en otra tienda.",
  "pos.identify.choice.checkedIn": "Entrada fichada.",
  "pos.identify.choice.checkedOut": "Salida fichada.",
  "pos.shift.title": "El cajón",
  "pos.shift.failed": "Eso no funcionó. Inténtelo de nuevo.",
  "pos.shift.handOver": "Entregar",
  "pos.shift.noneOpen":
    "No hay ningún turno abierto. Cuente el fondo y abra uno para empezar a cobrar.",
  "pos.shift.openingFloat": "Fondo de apertura",
  "pos.shift.open": "Abrir el turno",
  "pos.shift.openNow": "Abrir",
  "pos.shift.number": "Turno {number}",
  "pos.shift.openedBy": "Abierto por",
  "pos.shift.bills": "Cuentas cobradas",
  "pos.shift.byMethod": "Cobrado, por forma de pago",
  "pos.shift.nothingYet": "Todavía no se ha cobrado nada.",
  "pos.shift.expectedInDrawer": "Se espera en el cajón",
  "pos.shift.railCounts": "Lo que dice cada canal",
  "pos.shift.railCountsHint":
    "Revise la app del banco o el terminal. Deje en blanco lo que no revisó.",
  "pos.shift.cardNotInDrawer":
    "Lo cobrado con tarjeta y en línea no está en el cajón, así que no se cuenta aquí.",
  "pos.shift.counted": "Contado",
  "pos.shift.balances": "Cuadra.",
  "pos.shift.short": "Faltan {amount}",
  "pos.shift.over": "Sobran {amount}",
  "pos.shift.close": "Cerrar el turno",
  "pos.settle.title": "Cobrar",
  "pos.settle.total": "Total",
  "pos.settle.stillToPay": "Falta por pagar",
  "pos.settle.change": "Cambio",
  "pos.settle.tip": "Propina",
  "pos.settle.noTip": "Sin propina",
  "pos.settle.tipOther": "Otro monto",
  "pos.settle.due": "A pagar",
  "pos.station.title": "Cocina",
  "pos.station.backToTill": "Caja",
  "pos.station.all": "Todas las estaciones",
  "pos.station.empty": "Nada por preparar.",
  "pos.station.waiting": "{minutes} min",
  "pos.station.course": "Tiempo {number}",
  "pos.settle.amountTendered": "Monto recibido",
  "pos.settle.back": "Atrás",
  "pos.settle.done": "Listo",
  "pos.billActions.title": "Acciones de la cuenta",
  "pos.billActions.merge": "Unir",
  "pos.billActions.mergeHint": "Meta otra cuenta abierta en esta. Aquella se cierra.",
  "pos.billActions.noOthers": "No hay ninguna otra cuenta abierta.",
  "pos.billActions.split": "Dividir",
  "pos.billActions.splitHint": "Pase renglones a una cuenta aparte.",
  "pos.billActions.splitDo": "Pasar a una cuenta nueva",
  "pos.billActions.noFired": "Solo se pueden pasar los renglones que ya tiene una estación.",
  "pos.billActions.needsConnection": "Unir y dividir requieren conexión.",
  "pos.billActions.back": "Atrás",
  "pos.line.title": "Modificar renglón",
  "pos.line.void": "Anular",
  "pos.line.return": "Devolver",
  "pos.line.back": "Atrás",
  "pos.discount.title": "Descuento",
  "pos.discount.amount": "Monto del descuento",
  "pos.discount.tooLarge": "Un descuento no puede exceder la cuenta.",
  "pos.discount.back": "Atrás",
  "pos.discount.clear": "Limpiar",
  "pos.discount.apply": "Aplicar",
  "pos.table.title": "Mesa",
  "pos.table.seats": "{count} lugares",
  "pos.table.howMany": "{table}: ¿cuántos?",
  "pos.table.status.free": "Libre",
  "pos.table.status.seated": "Sentados",
  "pos.table.status.ordered": "Con pedido",
  "pos.table.status.billRequested": "Pidió la cuenta",
  "pos.signIn.title": "Configure esta caja",
  "pos.signIn.subtitle":
    "Se aprueba desde la administración. Después el personal entra escaneando.",
  "pos.signIn.expiredTitle": "Vuelva a entrar en esta caja",
  "pos.signIn.expiredSubtitle":
    "La sesión de este terminal terminó. No se ha perdido nada: lo que siga esperando se enviará en cuanto vuelva a entrar.",
  "pos.signIn.codeLabel": "Código de emparejamiento",
  "pos.signIn.instruction":
    "Pida a un gerente que abra {uri} e ingrese este código, o que lo escanee.",
  "pos.signIn.waiting": "Esperando aprobación…",
  "pos.signIn.retrying": "Reintentando — comprobando la conexión…",
  "pos.signIn.denied": "Se rechazó la solicitud de emparejamiento.",
  "pos.signIn.failed": "Algo salió mal.",
  "pos.signIn.newCode": "Mostrar un código nuevo",

  "staff.signIn.title": "Iniciar sesión",
  "staff.signIn.subtitle": "Use su passkey, o su nombre de usuario y contraseña.",
  "staff.signIn.passkey": "Iniciar sesión con una passkey",
  "staff.signIn.or": "o",
  "staff.signIn.username": "Nombre de usuario",
  "staff.signIn.password": "Contraseña",
  "staff.signIn.connect": "Iniciar sesión",
  "staff.signIn.rejected": "Esos datos no funcionaron.",
  "staff.signIn.passkeyRejected": "Esa passkey no funcionó.",
  "staff.signIn.unreachable": "No se pudo comunicar con el servidor.",
  "staff.home.greeting": "Sesión iniciada como",
  "staff.home.signOut": "Cerrar sesión",
  "staff.home.scan": "Escanear un código de caja",
  "staff.home.scanRefused": "Eso no parece un código de caja. Introdúzcalo a continuación.",
  "staff.home.enterCode": "Introducir un código de caja",
  "staff.home.enterCodePlaceholder": "p. ej. ABCD-1234",
  "staff.home.enterCodeSubmit": "Continuar",
  "staff.home.passkeyOffer":
    "Inicie sesión más rápido la próxima vez con una passkey en este teléfono.",
  "staff.home.passkeyAdd": "Añadir una passkey",
  "staff.home.passkeyAdded":
    "Passkey añadida. La próxima vez, elija Iniciar sesión con una passkey.",
  "staff.home.passkeyStale":
    "Por su seguridad, cierre sesión y vuelva a iniciar sesión, luego añada la passkey.",
  "staff.home.passkeyFailed": "No se pudo añadir una passkey en este dispositivo.",

  "staff.approve.continueAfterSignIn": "Iniciar sesión continuará con la solicitud de la caja.",
  "staff.approve.checking": "Comprobando el código…",
  "staff.approve.instruction":
    "Compruebe que coincide con el código que muestra la caja y confirme.",
  "staff.approve.take": "Soy yo",
  "staff.approve.notMe": "No soy yo",
  "staff.approve.approved": "Continúe en la caja.",
  "staff.approve.backHome": "Volver al inicio",
  "staff.approve.notForTill":
    "Este código empareja una caja, no identifica a quién está frente a ella.",
  "staff.approve.taken": "Otra persona ya está aprobando este código.",
  "staff.approve.used": "Este código ya se ha usado.",
  "staff.approve.gone": "Este código ya no es válido.",
  "staff.approve.refused": "Esta cuenta no puede tomar una caja.",
  "staff.approve.retry": "Intentar de nuevo",

  "staff.approve.someoneElse": "Otra persona",
  "staff.approve.pickEmployee": "Poner a alguien en esta caja",
  "staff.approve.search": "Buscar",
  "staff.approve.confirmFor": "¿Poner a {name} en esta caja?",
  "staff.approve.assigned": "{name} iniciará sesión en la caja en unos segundos.",
  "staff.approve.assignRefused": "No se pudo poner a esa persona en la caja.",

  "diner.title": "Pedido",
  "diner.scanPrompt": "Escanee el código de su mesa para ver el menú.",
  "diner.takeaway": "Para llevar",
  "diner.closed": "Los pedidos están cerrados en este momento.",
  "diner.all": "Todo",
  "diner.soldOut": "Agotado",
  "voice.newOrder": "Pedido nuevo recibido.",
  "diner.sold": "{count} vendidos",
  "diner.sending": "Enviando…",
  "diner.order": "Pedir {count} · {total}",
  "diner.sendFailed": "No se pudo enviar eso.",
  "diner.locating": "Comprobando dónde está…",
  "diner.location.denied":
    "Este restaurante necesita su ubicación para aceptar un pedido. Permita el acceso a la ubicación e inténtelo de nuevo.",
  "diner.location.unavailable":
    "No pudimos encontrar su ubicación. Compruebe que la ubicación esté activada e inténtelo de nuevo.",
  "diner.chooseOne": "· elija una",
  "diner.chooseRange": "· elija {min}–{max}",
  "diner.chooseUpTo": "· hasta {max}",
  "diner.extras": "Extras",
  "diner.noteLabel": "¿Algo que debamos saber?",
  "diner.notePlaceholder": "Sin chile, por favor",
  "diner.chooseGroup": "Elija {group}",
  "diner.add": "Agregar · {total}",
  "diner.yourNumber": "Su número",
  "diner.status.pending": "Enviado a preparar",
  "diner.status.accepted": "Aceptado",
  "diner.status.preparing": "Preparándose",
  "diner.status.served": "Servido",
  "diner.discount": "Descuento",
  "diner.service": "Servicio",
  "diner.total": "Total",
  "diner.paid": "Pagado, gracias.",
  "diner.payFirst": "Pague para confirmar su pedido, por favor.",
  "diner.payAtCounter": "Pague en el mostrador cuando guste.",
  "diner.couponCode": "Código de cupón",
  "diner.couponFailed": "Ese código no funcionó.",
  "diner.member.signIn": "Entrar",
  "diner.member.signInTitle": "Su membresía",
  "diner.member.signInExplainer":
    "Entre con su número de teléfono para ver sus puntos, su saldo y sus cupones, y para acumular en esta cuenta.",
  "diner.member.phone": "Número de teléfono",
  "diner.member.sendCode": "Envíenme un código",
  "diner.member.codeSent": "Le enviamos un código al {phone}.",
  "diner.member.devCode":
    "Este servidor no tiene configurado un proveedor de SMS, así que el código se muestra aquí: {code}",
  "diner.member.code": "Código",
  "diner.member.nameOptional": "Su nombre (solo si es nuevo aquí)",
  "diner.member.differentNumber": "Usar otro número",
  "diner.member.failed": "Eso no funcionó. Inténtelo de nuevo.",
  "diner.member.title": "Su membresía",
  "diner.member.number": "Cliente {number}",
  "diner.member.points": "Puntos",
  "diner.member.points.short": "{points} pts",
  "diner.member.pointsWorth": "Valen {amount} aquí",
  "diner.member.noPointsScheme": "Este restaurante no maneja un programa de puntos.",
  "diner.member.pointsHistory": "Puntos recientes",
  "diner.member.balance": "Saldo",
  "diner.member.held": "{amount} está retenido contra una cuenta abierta",
  "diner.member.pinNeeded": "Se le pedirá su PIN cuando pague con esto.",
  "diner.member.topUp": "Recargar",
  "diner.member.topUpAtCounter":
    "Pida la recarga en el mostrador: todavía no se puede hacer desde aquí.",
  "diner.member.getsYou": "le da {amount}",
  "diner.member.coupons": "Cupones",
  "diner.member.noCoupons": "Todavía no tiene cupones.",
  "diner.member.minSpend": "Gaste al menos {amount}",
  "diner.member.coupon.issued": "Listo",
  "diner.member.coupon.used": "Usado",
  "diner.member.coupon.expired": "Vencido",
  "diner.member.coupon.void": "Anulado",
  "diner.member.earnOnThisBill": "Entre para acumular puntos en esta cuenta.",
  "diner.member.addToThisBill": "Agregue a {name} a esta cuenta para acumular puntos.",
  "diner.member.claim": "Agrégame a esta cuenta",
  "diner.member.onThisBill": "{name} está en esta cuenta.",
  "diner.apply": "Aplicar",
  "diner.orderMore": "Pedir más",
  "diner.cancelled": "Este pedido se canceló",
  "reports.title": "Reportes",
  "reports.description": "Los números detrás del servicio, en su propio día de operación.",
  "reports.pick": "Reporte",
  "reports.from": "Desde",
  "reports.to": "Hasta",
  "reports.consolidate": "Unir el periodo en un solo renglón",
  "reports.groupBy": "Agrupar por",
  "reports.columns": "Columnas",
  "reports.export": "Exportar",
  "reports.exported": "En cola. Aparecerá en Exportaciones.",
  "reports.total": "Total",
  "reports.empty": "No se cobró nada en este periodo.",
  "reports.master": "Reporte maestro",
  "reports.itemCategory": "Artículos y categorías",
  "reports.paymentMethods": "Formas de pago",
  "reports.hourlySales": "Ventas por hora",
  "reports.discounts": "Descuentos",
  "reports.voids": "Anulaciones",
  "reports.topSelling": "Lo que más se vende",
  "reports.daily": "Diario",
  "reports.tables": "Mesas",
  "reports.shifts": "Turnos",
  "reports.credit": "Cuentas por cobrar",
  "reports.stock": "Movimiento de existencias",
  "reports.column.initialStock": "Existencia inicial",
  "reports.column.inbound": "Entradas",
  "reports.column.outbound": "Salidas",
  "reports.column.endingStock": "Existencia final",
  "reports.column.group": "Desglose",
  "reports.column.store": "Sucursal",
  "reports.column.orders": "Pedidos",
  "reports.column.covers": "Comensales",
  "reports.column.grossSales": "Ventas brutas",
  "reports.column.discount": "Descuento",
  "reports.column.campaignDiscount": "Descuento de campaña",
  "reports.column.netExTax": "Neto sin impuesto",
  "reports.column.netSales": "Ventas netas",
  "reports.column.tax": "Impuesto",
  "reports.column.serviceCharge": "Cargo por servicio",
  "reports.column.netIncTax": "Neto con impuesto",
  "reports.column.payments": "Cobrado",
  "reports.column.refunds": "Reembolsado",
  "reports.column.averageOrder": "Cuenta promedio",
  "reports.column.averageCover": "Promedio por comensal",
  "reports.column.quantity": "Cantidad",
  "reports.column.method": "Forma de pago",
  "reports.column.tenders": "Cobros",
  "reports.column.taken": "Cobrado",
  "reports.column.net": "Neto",
  "reports.column.hour": "Hora",
  "reports.column.orderNo": "Pedido",
  "reports.column.date": "Fecha",
  "reports.column.who": "Quién",
  "reports.column.what": "Qué",
  "reports.column.detail": "Detalle",
  "reports.column.reason": "Motivo",
  "reports.column.item": "Artículo",
  "reports.column.rankByQuantity": "Ordenar por cantidad",
  "reports.column.rankByRevenue": "Ordenar por ingreso",
  "reports.column.table": "Mesa",
  "reports.column.turnTime": "Minutos por ocupación",
  "reports.column.shift": "Turno",
  "reports.column.openedBy": "Abierto por",
  "reports.column.closedBy": "Cerrado por",
  "reports.column.float": "Fondo",
  "reports.column.expectedCash": "El cajón debería tener",
  "reports.column.countedCash": "Contado",
  "reports.column.difference": "Diferencia",
  "reports.column.customer": "Cliente",
  "reports.column.amount": "Monto",
  "reports.column.settled": "Saldado",
  "reports.column.outstanding": "Pendiente",
  "reports.column.ageDays": "Días abierta",
  "reports.column.status": "Estado",
  "reports.hourlySalesDetails": "Ventas por hora y artículo",
  "reports.newItems": "Platillos nuevos",
  "reports.coupons": "Cupones",
  "reports.discountPromos": "Campañas promocionales",
  "reports.sales": "Ventas por empleado",
  "reports.items": "Artículos y merma",
  "reports.storeCosts": "Gastos de la sucursal",
  "reports.itemCosts": "Costos por artículo",
  "reports.profit": "Utilidad",
  "reports.tipReporting": "Reporte de propinas (8027)",
  "reports.tipsByOccupation": "Propinas por ocupación",
  "reports.labourHours": "Horas trabajadas",
  "reports.column.grossReceipts": "Ingresos brutos",
  "reports.column.chargedTips": "Propinas con tarjeta",
  "reports.column.chargeReceipts": "Cobros con tarjeta que llevaron propina",
  "reports.column.cashTips": "Propinas en efectivo",
  "reports.column.directlyTipped": "Pagado a quien recibe directamente",
  "reports.column.indirectlyTipped": "Pagado a quien recibe indirectamente",
  "reports.column.eightPercent": "8 % de los ingresos brutos",
  "reports.column.job": "Puesto",
  "reports.column.occupationCode": "Código de ocupación",
  "reports.column.tipStatus": "Estado de propina",
  "reports.column.businessDate": "Día comercial",
  "reports.column.openEntries": "Sin marcar salida",
  "reports.column.minutes": "Minutos",
  "reports.unattributed": "Sin empleado",
  "reports.column.averageAmount": "Monto promedio",
  "reports.column.addedOn": "Agregado",
  "reports.column.labels": "Etiquetas",
  "reports.column.quantityShare": "Parte de la cantidad",
  "reports.column.amountShare": "Parte de las ventas",
  "reports.column.coupon": "Cupón",
  "reports.column.issued": "Emitidos",
  "reports.column.redeemed": "Canjeados",
  "reports.column.expired": "Vencidos",
  "reports.column.redemptionRate": "Tasa de canje",
  "reports.column.orderAmount": "Total de la cuenta",
  "reports.column.discountShare": "Descuento como parte de las cuentas",
  "reports.column.promo": "Campaña",
  "reports.column.returnedQuantity": "Devueltos",
  "reports.column.voidedQuantity": "Anulados",
  "reports.column.wastageRate": "Tasa de merma",
  "reports.column.received": "Recibido",
  "reports.column.supplyCost": "Costo de insumos",
  "reports.column.marketingCost": "Marketing",
  "reports.column.laborCost": "Sueldos",
  "reports.column.administrativeCost": "Gastos generales",
  "reports.column.adHocCost": "Extraordinarios",
  "reports.column.totalCosts": "Costos totales",
  "reports.column.profit": "Utilidad",
  "reports.column.grossMargin": "Margen",
  "reports.column.openingCash": "Efectivo inicial",
  "reports.column.salesQty": "Cantidad vendida",
  "reports.column.averageCost": "Costo promedio",
  "reports.column.originalPaid": "Antes del descuento",
  "reports.column.actualPaid": "Después del descuento",
  "reports.column.marginBeforeDiscount": "Margen antes del descuento",
  "reports.column.marginAfterDiscount": "Margen después del descuento",
  "reports.column.creditTaken": "Puesto a cuenta",
  "reports.column.expense": "Pagado",
  "reports.column.profitBalance": "Saldo de utilidad",
  "costs.title": "Gastos de la sucursal",
  "costs.description": "Lo que gasta el negocio, junto a lo que cobra.",
  "costs.add": "Agregar un gasto",
  "costs.edit": "Editar un gasto",
  "costs.date": "Día de operación",
  "costs.type": "Para qué",
  "costs.amount": "Monto",
  "costs.note": "Nota",
  "costs.category": "Grupo",
  "costs.enteredBy": "Capturado por",
  "costs.empty": "No se ha capturado nada en este periodo.",
  "costs.deleteTitle": "¿Quitar este gasto?",
  "costs.deleteBody":
    "El reporte de gastos de la sucursal deja de contarlo. Lo que la sucursal cobró ese día no cambia.",
  "costs.record": "Ficha",
  "costs.category.marketing": "Marketing",
  "costs.category.labor": "Personal",
  "costs.category.administrative": "Gastos generales",
  "costs.category.ad_hoc": "Extraordinario",
  "costs.type.platformFee": "Comisión de plataforma",
  "costs.type.advertising": "Publicidad",
  "costs.type.marketingDiscounts": "Descuentos promocionales",
  "costs.type.deliveryFee": "Envío y reparto",
  "costs.type.serviceFee": "Comisión por servicio",
  "costs.type.salary": "Sueldo",
  "costs.type.salesCommission": "Comisión por ventas",
  "costs.type.overtime": "Horas extra",
  "costs.type.insurance": "Seguro",
  "costs.type.benefitsTraining": "Prestaciones y capacitación",
  "costs.type.accommodation": "Apoyo de vivienda",
  "costs.type.transportation": "Apoyo de transporte",
  "costs.type.travel": "Viáticos",
  "costs.type.rent": "Renta",
  "costs.type.propertyFee": "Cuotas del inmueble",
  "costs.type.water": "Agua",
  "costs.type.electricity": "Luz",
  "costs.type.gas": "Gas",
  "costs.type.renovation": "Adecuación, amortizada",
  "costs.type.equipmentPurchase": "Equipo comprado",
  "costs.type.equipmentRepair": "Reparación de equipo",
  "costs.type.depreciation": "Depreciación de equipo",
  "costs.type.adHoc": "Gasto extraordinario",
  "exports.title": "Exportaciones",
  "exports.description": "Archivos que pidió, y si ya están listos.",
  "exports.report": "Reporte",
  "exports.period": "Periodo",
  "exports.rows": "Renglones",
  "exports.requestedBy": "Pedido por",
  "exports.requested": "Solicitado",
  "exports.download": "Descargar",
  "exports.expiresOn": "Disponible hasta el {date}",
  "exports.expired": "Ya no está disponible. Pídalo de nuevo.",
  "exports.record": "Exportación",
  "exports.completed": "Terminado",
  "exports.availability": "Disponibilidad",
  "exports.failure": "Por qué falló",
  "exportStatus.queued": "Esperando",
  "exportStatus.running": "En proceso",
  "exportStatus.ready": "Listo",
  "exportStatus.failed": "Falló",

  "dashboard.title": "Hoy",
  "dashboard.description": "Cómo va el día, contra el mismo día de la semana pasada.",
  "dashboard.updated": "Actualizado",
  "dashboard.refresh": "Actualizar ahora",
  "dashboard.vsLastWeek": "vs. el mismo día de la semana pasada",
  "dashboard.byMethod": "Por forma de pago",
  "dashboard.ranking": "Lo que más se vende",
  "dashboard.daily": "Por día",
  "dashboard.money.totalSales": "Ventas totales",
  "dashboard.money.received": "Recibido",
  "dashboard.money.credit": "A cuenta",
  "dashboard.money.unpaid": "Todavía se debe",
  "dashboard.money.discount": "Descuentos",
  "dashboard.money.refund": "Reembolsado",
  "dashboard.money.cancelled": "Cancelado",
  "dashboard.counts.orders": "Pedidos",
  "dashboard.counts.receivedOrders": "Pagados",
  "dashboard.counts.creditOrders": "A cuenta",
  "dashboard.counts.unpaidOrders": "Abiertos",
  "dashboard.counts.discountOrders": "Con descuento",
  "dashboard.counts.refundOrders": "Reembolsados",
  "dashboard.counts.cancelledOrders": "Cancelados",

  "auth.signIn": "Entrar",
  "auth.signOut": "Salir",
  "auth.email": "Correo electrónico",
  "auth.password": "Contraseña",
  "auth.signingIn": "Entrando…",
  "auth.invalidCredentials": "Ese correo y esa contraseña no corresponden a ninguna cuenta.",
  "auth.title": "Entrar a TOVI",
  "auth.subtitle": "Administración",
  "auth.showPassword": "Mostrar la contraseña",
  "auth.operatorTitle": "Entrar a TOVI Operator",
  "auth.operatorSubtitle": "Solo personal de la plataforma",
  "auth.operatorAccountTitle": "Esta cuenta pertenece a la consola de operador.",
  "auth.operatorAccountBody": "Entra con una cuenta de comercio para usar la administración.",
  "auth.merchantAccountTitle": "Esta cuenta pertenece a la administración del comercio.",
  "auth.merchantAccountBody": "Entra con una cuenta de operador para usar esta consola.",
  "auth.accountUnusableTitle": "Esta cuenta no puede usar esta consola.",
  "auth.accountUnusableBody":
    "Puede estar desactivada o sin terminar de configurar. Pide a quien la administra que la revise.",

  "shell.store": "Sucursal",
  "shell.role": "Puesto",
  "shell.language": "Idioma",
  "shell.loading": "Cargando…",
  "shell.openNav": "Abrir la navegación",
  "shell.closeNav": "Cerrar la navegación",
  "shell.notifications": "Notificaciones",
  "shell.account": "Menú de la cuenta",
  "shell.venue": "Local actual",
  "shell.settings": "Configuración",
  "shell.emptyTitle": "Todavía no hay nada aquí",
  "shell.emptyBody":
    "Este es el armazón autenticado. El menú, los pedidos y los reportes llegan en fases posteriores.",

  "error.generic": "Algo salió mal.",
  "error.notFound": "No se encontró.",
  "error.forbidden": "No tiene permiso para hacer eso.",
  "error.notEntitled": "Eso no es parte de su plan.",
  "error.quotaExceeded": "Ha llegado a su límite.",

  /**
   * A refusal a merchant or a cashier actually meets, keyed by the `reason` the
   * API sends beside the code (`packages/shared/src/http/error-reasons.ts`).
   * The four keys above are the generic sentences; these are the specific ones,
   * and `resolveErrorMessage` prefers them over the server's English.
   */
  "error.inventoryForbidden": "No tiene acceso de inventario a eso.",
  "error.capabilityNotDelegated": "Su casa matriz no ha habilitado eso para esta tienda.",
  "error.rateLimited": "Demasiados intentos — inténtelo de nuevo en un momento.",
  "error.signInRequired": "Inicie sesión para continuar.",
  "error.accountDisabled": "Esta cuenta está deshabilitada.",
  "error.accountNoStore": "Esta cuenta no está asociada a ninguna tienda.",
  "error.storeNotAllowed": "No puede trabajar en esa tienda.",
  "error.pinRequired": "Inicie sesión en esta caja para continuar.",
  "error.pinSessionExpired": "Su sesión en esta caja ha caducado. Escanee de nuevo.",
  "error.pinMismatch": "Ese PIN no coincide.",
  "error.terminalRequired": "Solo un terminal puede identificar a un cajero.",
  "error.grantNotRecognised": "Esa sesión ha caducado. Vuelva a escanear el código.",
  "error.deviceCodeNotPending": "Ese código no se puede aprobar en nombre de otra persona.",
  "error.alreadyClockedIn": "Ya ha fichado la entrada.",
  "error.notClockedIn": "No ha fichado la entrada.",
  "error.clockedInElsewhere": "Ha fichado la entrada en otra tienda, no aquí.",
  "error.unitInUse": "Hay insumos almacenados en esta unidad.",
  "error.unitSeeded":
    "Una unidad predefinida no se puede eliminar, solo ocultar mediante el orden.",
  "error.unitSelfConversion": "Una unidad se convierte a sí misma uno a uno.",
  "error.unitDimensionMismatch": "Esas dos unidades miden cosas distintas y no se convierten.",
  "error.supplyCategoryHasChildren": "Vacíe primero las subcategorías.",
  "error.supplyCategoryInUse": "Hay insumos archivados en esta categoría.",
  "error.supplyCategorySelfParent": "Una categoría no puede contenerse a sí misma.",
  "error.supplyCategoryTooDeep": "Las categorías llegan a dos niveles, no más.",
  "error.brandInUse": "Hay insumos con esta marca.",
  "error.supplyHasStock": "Hay existencias de este insumo. Regularícelas primero.",
  "error.supplyNumberTaken": "Ese número ya está en uso.",
  "error.barcodeTaken": "Ese código de barras ya pertenece a un insumo.",
  "error.menuCategoryInUse": "Mueva o elimine primero los artículos de esta categoría.",
  "error.itemNumberTaken": "Otro artículo ya usa ese código.",
  "error.tableAreaInUse": "Mueva o elimine primero las mesas de esta zona.",
  "error.stationHasPrinters": "Mueva primero las impresoras de esta estación.",
  "error.stationHasItems": "Redirija primero los artículos de esta estación.",
  "error.printerHasItems":
    "Algunos artículos solo se imprimen en esta impresora. Redirígalos primero.",
  "error.printerNeedsAddress": "Dé una dirección a esta impresora antes de emparejarla.",
  "error.printerNeedsStation":
    "Una impresora de estación necesita una estación a la que dirigir los artículos.",
  "error.paymentMethodBuiltIn":
    "Ese método de pago viene incorporado y no se puede eliminar. Desactívelo en su lugar.",
  "error.tipPoolPaidOut": "Ese fondo ya repartió. Desactívelo en lugar de eliminarlo.",
  "error.tableAlreadyHasBill": "Esta mesa ya tiene una cuenta abierta.",
  "error.billAlreadySettled": "Esta cuenta ya fue liquidada.",
  "error.billNotCheckedOut": "Esta cuenta no ha sido cerrada.",
  "error.orderCancelled": "Este pedido fue cancelado.",
  "error.settledBillNeedsReverse": "Revierta el cierre antes de modificar una cuenta liquidada.",
  "error.moneyTakenUseRefund": "Ya se cobró dinero; reembolse o revierta en su lugar.",
  "error.nothingTakenYet": "Todavía no se ha cobrado nada.",
  "error.nothingLeftToRefund": "No queda nada por reembolsar.",
  "error.refundExceedsTaken": "Eso es más de lo que se cobró.",
  "error.paymentExceedsOutstanding": "Eso es más de lo que queda por pagar.",
  "error.paymentMethodUnavailable": "Ese método de pago no está disponible aquí.",
  "error.paymentProviderNotInstalled":
    "Ese método de pago nombra un proveedor que no está instalado.",
  "error.paymentRailPending": "Ese pago no ha terminado; inténtelo de nuevo en un momento.",
  "error.paymentAsyncUnsupported":
    "Ese método de pago se liquida después de que el pagador actúe, y esta caja aún no puede esperar.",
  "error.discountExceedsBill": "Un descuento no puede superar la cuenta.",
  "error.returnExceedsOrdered": "Se devolvió más de lo que se pidió.",
  "error.takingsCounted":
    "La recaudación de ese día ya fue contada. Una propina solo se puede cambiar antes del cierre del día.",
  "error.tipNotSupportedOnTender": "A ese cobro no se le puede añadir propina.",
  "error.mergeIntoSelf": "Un pedido no se puede fusionar consigo mismo.",
  "error.mergeSourcePaid": "Esa cuenta ya cobró dinero.",
  "error.splitNoSeats": "Ninguna línea de esta cuenta está asignada a un puesto.",
  "error.splitSingleSeat":
    "Todas las líneas están en un mismo puesto, así que no hay nada que dividir.",
  "error.splitLeavesEmptyBill": "Separar todas las líneas dejaría una cuenta vacía.",
  "error.courseNothingWaiting": "No hay nada de ese tiempo esperando a prepararse.",
  "error.giftCardCodeRequired": "¿Qué tarjeta de regalo? Hace falta un código para canjearla.",
  "error.giftCardVoided": "Esa tarjeta fue anulada.",
  "error.giftCardExpired": "Esa tarjeta ha caducado.",
  "error.giftCardCurrencyMismatch": "Esa tarjeta está en otra moneda.",
  "error.giftCardInsufficient": "Esa tarjeta no tiene tanto saldo.",
  "error.memberRequired": "Indique quién es el cliente antes de usar su saldo.",
  "error.memberBlocked": "Ese cliente está bloqueado.",
  "error.memberBalanceInsufficient": "Eso es más que el saldo del cliente.",
  "error.memberPointsInsufficient": "Eso es más puntos de los que tiene el cliente.",
  "error.tabClosed": "Esa cuenta a crédito ya está cerrada.",
  "error.tabNothingLeft": "No queda nada en esta cuenta a crédito.",
  "error.tabRepayExceeds": "Eso es más de lo que debe la cuenta a crédito.",
  "error.tabRepaidWithTab": "Una cuenta a crédito no se puede pagar con otra.",
  "error.couponSwitchedOff": "Ese cupón está desactivado.",
  "error.couponUsed": "Ese cupón ya fue usado.",
  "error.couponExpired": "Ese cupón ha caducado.",
  "error.couponNotYetValid": "Ese cupón todavía no es válido.",
  "error.couponOtherMember": "Ese cupón pertenece a un cliente que no está en esta cuenta.",
  "error.couponBillEmpty": "Todavía no hay nada en esta cuenta.",
  "error.couponBelowMinimum": "Esta cuenta no alcanza el mínimo del cupón.",
  "error.orderLocationRequired": "Este restaurante necesita su ubicación para aceptar un pedido.",
  "error.orderTooFar": "Está demasiado lejos del restaurante para pedir.",
  "error.shiftAlreadyOpen": "Ya hay un turno abierto. Ciérrelo antes de abrir otro.",
  "error.shiftAlreadyClosed": "Ese turno ya está cerrado.",
  "error.noShiftOpen": "No hay ningún turno abierto.",
  "error.shiftTenderNothingTaken": "Este turno no recibió nada en uno de esos métodos.",
  "error.lastRoleEditor": "Este es el último puesto que puede editar puestos.",
  "error.photoNotJpeg": "Esa no es una imagen JPEG.",
  "error.photoTooLarge": "Esa foto es demasiado grande.",
  "error.passwordForSelf": "No puede establecer su propia contraseña aquí.",
  "error.employeeOutranksYou":
    "Esta persona puede hacer cosas que su puesto no puede, por lo que no puede establecer su contraseña.",
  "error.roleOutranksYou": "No puede asignar un puesto que pueda hacer más que el suyo.",

  "operator.shell.console": "Consola de operador",
  "operator.shell.noRole": "Sin rol",

  "operator.nav.overview": "Resumen",
  "operator.nav.merchants": "Comercios",
  "operator.nav.catalog": "Catálogo",
  "operator.nav.finance": "Finanzas",
  "operator.nav.platform": "Plataforma",

  "operator.field.merchant": "Comercio",
  "operator.field.merchants": "Comercios",
  "operator.field.plan": "Plan",
  "operator.field.status": "Estado",
  "operator.field.country": "País",
  "operator.field.partner": "Socio",
  "operator.field.code": "Código",
  "operator.field.scope": "Alcance",
  "operator.field.price": "Precio",
  "operator.field.tier": "Nivel",
  "operator.field.expires": "Vence",
  "operator.field.stores": "Locales",
  "operator.field.transactions": "Transacciones",
  "operator.field.permanent": "Permanente",
  "operator.field.perMonth": "{price}/mes",
  "operator.field.monthly": "Mensual",
  "operator.field.yearly": "Anual",

  "operator.dashboard.title": "Panel",
  "operator.dashboard.period": "{start} a {end}",
  "operator.dashboard.loadFailed": "No se pudo cargar el panel.",
  "operator.dashboard.new": "Comercios nuevos",
  "operator.dashboard.newHint": "Agregados en este periodo",
  "operator.dashboard.cumulative": "Acumulado",
  "operator.dashboard.cumulativeHint": "Total en la lista",
  "operator.dashboard.active": "Activos",
  "operator.dashboard.activeHint": "Con monto transaccionado mayor que cero",
  "operator.dashboard.activeRate": "Tasa de actividad",
  "operator.dashboard.noPreviousPeriod": "Sin periodo anterior",
  "operator.dashboard.monthOnMonth": "{value} contra el mes anterior",
  "operator.dashboard.revenue": "Ingresos por suscripción",
  "operator.dashboard.transactionsHint": "Pagos de suscripción",
  "operator.dashboard.transactionAmount": "Monto transaccionado",
  "operator.dashboard.pendingSettlement": "Pendiente de liquidar",
  "operator.dashboard.settled": "Liquidado",
  "operator.dashboard.allTime": "Desde siempre",
  "operator.dashboard.cumulativeRevenue": "Ingresos acumulados totales",
  "operator.dashboard.pendingPlusSettled": "Pendiente más liquidado",

  "operator.merchants.title": "Comercios",
  "operator.merchants.description":
    "Todos los comercios de la plataforma, con su plan y su estado de suscripción actuales.",
  "operator.merchants.empty": "Ningún comercio coincide con esta búsqueda.",
  "operator.merchants.search": "Buscar comercios",
  "operator.merchants.subscription": "Suscripción",
  "operator.merchants.notProvisioned": "Sin aprovisionar",
  "operator.merchants.anySubscription": "Cualquier suscripción",
  "operator.merchants.anyStatus": "Cualquier estado",
  "operator.merchants.new": "Nuevo comercio",
  "operator.merchants.loadFailed": "No se pudo cargar este comercio.",
  "operator.merchants.created": "{country} · creado el {date}",
  "operator.merchants.signContract": "Firmar un contrato",
  "operator.merchants.profile": "Perfil",
  "operator.merchants.pilot": "Comercio piloto",
  "operator.merchants.channelPartner": "Socio de canal",
  "operator.merchants.direct": "Directo",
  "operator.merchants.bdStaff": "Ejecutivo comercial",
  "operator.merchants.afterSalesStaff": "Personal de posventa",
  "operator.merchants.settlementTimezone": "Zona horaria de liquidación",
  "operator.merchants.internalRemarks": "Notas internas",
  "operator.merchants.subscriptions": "Suscripciones",
  "operator.merchants.perMonthExpires": "{price} al mes · vence el {date}",
  "operator.merchants.manage": "Administrar",
  "operator.merchants.nothingProvisioned":
    "Todavía no hay nada aprovisionado. Firme un contrato para venderle un plan a este comercio.",
  "operator.merchants.contracts": "Contratos",
  "operator.merchants.contractSigned": "{plan} · firmado el {date}",
  "operator.merchants.noContracts": "Todavía no hay contratos.",
  "operator.merchants.users": "Usuarios de administración",
  "operator.merchants.createFailed": "No se pudo crear el comercio.",
  "operator.merchants.create": "Crear comercio",
  "operator.merchants.creating": "Creando…",
  "operator.merchants.name": "Nombre del comercio",
  "operator.merchants.countryHint":
    "El primer local se configura a partir de este mercado: {currencies}, {locales}.",
  "operator.merchants.countryHintEmpty":
    "Define el perfil de mercado con el que se configura el primer local.",
  "operator.merchants.currencyPair": "{first} y {second}",
  "operator.merchants.selectMarket": "Elija un mercado",
  "operator.merchants.firstStore": "Primer local",
  "operator.merchants.storeName": "Nombre del local",
  "operator.merchants.webAddress": "Dirección web",
  "operator.merchants.webAddressHint":
    "La usa el sitio de pedidos por QR. Minúsculas, letras, números y guiones.",
  "operator.merchants.ownerUsername": "Usuario del dueño",
  "operator.merchants.ownerName": "Nombre del dueño",
  "operator.merchants.ownerEmail": "Correo del dueño",
  "operator.merchants.ownerPassword": "Contraseña del dueño",
  "operator.merchants.ownerPasswordHint":
    "Al menos 12 caracteres. Entréguela de forma segura; no se vuelve a mostrar.",

  "operator.contracts.title": "Contratos",
  "operator.contracts.description":
    "Se firman desde la página del comercio. Un contrato enviado no se puede editar: un cambio es una renovación, una mejora de plan o un contrato nuevo.",
  "operator.contracts.empty": "No hay contratos.",
  "operator.contracts.number": "Contrato",
  "operator.contracts.signed": "Firmado",
  "operator.contracts.term": "Plazo",
  "operator.contracts.termValue": "{months} meses hasta el {date}",
  "operator.contracts.total": "Total",
  "operator.contracts.signTitle": "Firmar un contrato para {merchant}",
  "operator.contracts.locked":
    "Un contrato firmado no se puede editar. Un cambio posterior es una renovación, una mejora de plan o un contrato nuevo.",
  "operator.contracts.sign": "Firmar contrato",
  "operator.contracts.signing": "Firmando…",
  "operator.contracts.signFailed": "No se pudo firmar el contrato.",
  "operator.contracts.planVersion": "Versión del plan",
  "operator.contracts.choosePlan": "Elija un plan",
  "operator.contracts.planOption": "{name} — {price}/mes",
  "operator.contracts.billedTo": "Se cobra a",
  "operator.contracts.billedToHint":
    "Un contrato de cadena comparte el cupo entre todos los locales del grupo.",
  "operator.contracts.oneStore": "Un solo local",
  "operator.contracts.wholeChain": "Toda la cadena",
  "operator.contracts.store": "Local",
  "operator.contracts.cycle": "Ciclo de cobro",
  "operator.contracts.yearlyAnnual": "Anual, cobrado una vez al año",
  "operator.contracts.duration": "Duración en meses",
  "operator.contracts.signDate": "Fecha de firma",
  "operator.contracts.signDateHint": "La suscripción empieza en esta fecha.",
  "operator.contracts.trialMonths": "Meses de prueba gratis",
  "operator.contracts.trialMonthsHint":
    "Se suman al plazo. El plan vuelve solo cuando termina la prueba.",
  "operator.contracts.discount": "Descuento",
  "operator.contracts.discountHint": "En unidades menores de {currency}.",
  "operator.contracts.discountHintUnknown": "En unidades menores de la moneda del plan.",
  "operator.contracts.estimate":
    "Total estimado {total}. El servidor vuelve a calcular el precio al enviar.",

  "operator.subscriptions.title": "Suscripciones",
  "operator.subscriptions.description":
    "Lo que cada comercio tiene hoy, y las condiciones bajo las que se le vendió.",
  "operator.subscriptions.empty": "No hay suscripciones.",
  "operator.subscriptions.loadFailed": "No se pudo cargar esta suscripción.",
  "operator.subscriptions.heading": "{merchant} — {plan}",
  "operator.subscriptions.subheading": "Nivel {tier} · {scope}",
  "operator.subscriptions.invalidate": "Anular",
  "operator.subscriptions.renew": "Renovar",
  "operator.subscriptions.changePlan": "Cambiar de plan",
  "operator.subscriptions.addon": "Complemento",
  "operator.subscriptions.term": "Plazo",
  "operator.subscriptions.storedStatus": "Estado guardado",
  "operator.subscriptions.datesDisagree": "(las fechas dicen otra cosa)",
  "operator.subscriptions.starts": "Empieza",
  "operator.subscriptions.trialEnds": "La prueba termina",
  "operator.subscriptions.termLength": "Duración del plazo",
  "operator.subscriptions.months": "{months} meses",
  "operator.subscriptions.priceSnapshot": "Precio registrado",
  "operator.subscriptions.perMonth": "{price} al mes",
  "operator.subscriptions.quotaSnapshot": "Cupos registrados",
  "operator.subscriptions.unmetered": "Sin límite",
  "operator.subscriptions.quotaWithExtra": "{allowed} ({limit} + {extra} comprados)",
  "operator.subscriptions.features": "Funciones ({count})",
  "operator.subscriptions.featurePermanent": "permanente",
  "operator.subscriptions.addons": "Complementos",
  "operator.subscriptions.noAddons": "No se compró ninguno.",
  "operator.subscriptions.addonQuota": "{resource} × {quantity}",
  "operator.subscriptions.addonUntil": "Hasta el {date}",
  "operator.subscriptions.renewTitle": "Renovar la suscripción",
  "operator.subscriptions.addonTitle": "Comprar un complemento",
  "operator.subscriptions.apply": "Aplicar",
  "operator.subscriptions.applying": "Aplicando…",
  "operator.subscriptions.applyFailed": "No se pudo aplicar el cambio.",
  "operator.subscriptions.priceIt": "Calcular el precio",
  "operator.subscriptions.priceFailed": "No se pudo calcular el precio del cambio.",
  "operator.subscriptions.monthsLabel": "Meses",
  "operator.subscriptions.renewCycleHint":
    "Una renovación anticipada se suma al vencimiento actual, así que no se pierde el plazo que queda.",
  "operator.subscriptions.newPlan": "Plan nuevo",
  "operator.subscriptions.newPlanHint":
    "Solo un nivel igual o superior. No se permite bajar de nivel.",
  "operator.subscriptions.planTierOption": "{name} (nivel {tier})",
  "operator.subscriptions.charged": "Se cobra {amount} por el plazo que queda",
  "operator.subscriptions.credited": "Se acredita {amount} del plan actual",
  "operator.subscriptions.net": "Neto {amount}",
  "operator.subscriptions.addonType": "Tipo de complemento",
  "operator.subscriptions.extraQuota": "Cupo extra",
  "operator.subscriptions.valueAddedFeature": "Función de venta aparte",
  "operator.subscriptions.resource": "Recurso",
  "operator.subscriptions.extraUnits": "Unidades extra",
  "operator.subscriptions.feature": "Función",
  "operator.subscriptions.featureHint": "Se cobra por los días que le quedan al plazo principal.",
  "operator.subscriptions.chooseFeature": "Elija una función",
  "operator.subscriptions.invalidateTitle": "Anular la suscripción de {merchant}",
  "operator.subscriptions.invalidateWarning":
    "Esto no se puede deshacer. El comercio pierde de inmediato todas sus funciones y cupos, incluso lo que compró aparte, y la suscripción no se puede revivir con una renovación.",
  "operator.subscriptions.reason": "Motivo",
  "operator.subscriptions.reasonHint":
    "Queda registrado en la suscripción. Al menos diez caracteres.",
  "operator.subscriptions.requestCode": "Pedir un código",
  "operator.subscriptions.codeFailed": "No se pudo pedir un código.",
  "operator.subscriptions.invalidating": "Anulando…",
  "operator.subscriptions.invalidateFailed": "No se pudo anular.",
  "operator.subscriptions.verificationCode": "Código de verificación",
  "operator.subscriptions.codeHintDev": "Compilación de desarrollo: el código ya viene puesto.",
  "operator.subscriptions.codeHintSent": "Enviado al canal que tiene registrado.",

  "operator.plans.title": "Versiones de plan",
  "operator.plans.description":
    "Los precios, los cupos y las funciones incluidas son datos. Editarlos nunca cambia lo que una suscripción ya firmada compró.",
  "operator.plans.empty": "No hay versiones de plan.",
  "operator.plans.search": "Buscar planes",
  "operator.plans.yearlyPerMonth": "Anual, por mes",
  "operator.plans.annualSaving": "Ahorro anual",
  "operator.plans.quotas": "Cupos",
  "operator.plans.features": "Funciones",

  "operator.features.title": "Funciones",
  "operator.features.description":
    "El registro comercial del catálogo. Los códigos en sí son estructurales — son contra lo que verifican las compuertas de la API — así que no se editan aquí.",
  "operator.features.empty": "No hay funciones.",
  "operator.features.search": "Buscar funciones",
  "operator.features.feature": "Función",
  "operator.features.group": "Grupo",
  "operator.features.grades": "Se vende por niveles",
  "operator.features.ungraded": "Encendida o apagada",
  "operator.features.valueAdded": "Se vende aparte",

  "operator.hardware.title": "Equipos",
  "operator.hardware.description": "Se pueden incluir en un contrato.",
  "operator.hardware.empty": "No hay equipos.",
  "operator.hardware.search": "Buscar equipos",
  "operator.hardware.name": "Equipo",
  "operator.hardware.model": "Modelo",

  "operator.transactions.title": "Transacciones",
  "operator.transactions.description":
    "Pagos de suscripción, no ventas del restaurante. Este es el libro detrás de las cifras de ingresos del panel.",
  "operator.transactions.empty": "No hay transacciones.",
  "operator.transactions.serial": "Folio",
  "operator.transactions.product": "Producto",
  "operator.transactions.amount": "Monto",
  "operator.transactions.paid": "Pagado",
  "operator.transactions.settlement": "Liquidación",
  "operator.transactions.reconciled": "Conciliada",
  "operator.transactions.markReconciled": "Marcar como conciliada",

  "operator.partners.title": "Socios de canal",
  "operator.partners.description":
    "Revendedores que ganan comisión por las suscripciones que pagan sus comercios.",
  "operator.partners.empty": "No hay socios de canal.",
  "operator.partners.search": "Buscar socios",
  "operator.partners.partnerId": "ID del socio",
  "operator.partners.commission": "Comisión",
  "operator.partners.bonus": "Bono por primera firma",
  "operator.partners.settles": "Liquida",
  "operator.partners.settlesValue": "{from} → {to}",

  "operator.settlements.title": "Liquidaciones",
  "operator.settlements.description":
    "La comisión que la plataforma le debe a sus revendedores. Cerrar una corrida exige un auditor designado y un código de verificación, y no hay forma de borrarla.",
  "operator.settlements.empty": "No hay corridas de liquidación.",
  "operator.settlements.number": "Liquidación",
  "operator.settlements.period": "Periodo",
  "operator.settlements.periodValue": "{start} – {end}",
  "operator.settlements.gross": "Monto transaccionado",
  "operator.settlements.rate": "Tasa",
  "operator.settlements.due": "Por pagar",
  "operator.settlements.paid": "Pagado",
  "operator.settlements.auditor": "Auditor",

  "operator.referrals.title": "Referidos",
  "operator.referrals.description":
    "Comercios recomendados desde las terminales de otros comercios.",
  "operator.referrals.empty": "Todavía no hay referidos.",
  "operator.referrals.business": "Negocio",
  "operator.referrals.contact": "Contacto",
  "operator.referrals.phone": "Teléfono",
  "operator.referrals.referredBy": "Referido por",
  "operator.referrals.received": "Recibido",

  "operator.team.title": "Equipo",
  "operator.team.description": "Personal de la plataforma y cuentas de socios de canal.",
  "operator.team.empty": "No hay cuentas de operador.",
  "operator.team.name": "Nombre",
  "operator.team.username": "Usuario",
  "operator.team.email": "Correo",
  "operator.team.role": "Rol",
  "operator.team.scope": "Alcance",
  "operator.team.ownPartner": "Los comercios de su propio socio",
  "operator.team.platform": "Plataforma",
  "operator.team.search": "Buscar equipo",
  "operator.team.new": "Nuevo operador",
  "operator.team.edit": "Editar operador",
  "operator.team.record": "Registro",
  "operator.team.password": "Contraseña",
  "operator.team.passwordHint": "Al menos doce caracteres.",
  "operator.team.setPassword": "Establecer contraseña",
  "operator.team.setPasswordHint":
    "Cierra su sesión en todas partes, incluida la suya, si está restableciendo su propia contraseña.",
  "operator.team.deleteTitle": "¿Eliminar este operador?",
  "operator.team.deleteBody":
    "Ya no podrá iniciar sesión, y su usuario y correo quedan libres para una cuenta nueva. Su nombre permanece en los comercios que firmó y en cada fila de auditoría que escribió.",

  "operator.roles.title": "Roles",
  "operator.roles.description":
    "Roles de operador. Solo se pueden otorgar permisos de operador: un permiso de comercio aquí sería un permiso que nada llega a revisar.",
  "operator.roles.empty": "No hay roles.",
  "operator.roles.role": "Rol",
  "operator.roles.seeded": "De fábrica",
  "operator.roles.members": "Miembros",
  "operator.roles.permissions": "Permisos",
  "operator.roles.granted": "{count} otorgados",
  "operator.roles.new": "Nuevo rol",
  "operator.roles.name": "Nombre",
  "operator.roles.note": "Notas",
  "operator.roles.deleteTitle": "¿Eliminar este rol?",
  "operator.roles.deleteBody":
    "Nadie podrá asignarse a partir de ahora, y sus permisos se eliminan. Su código queda libre para reutilizarse.",
  "operator.roles.noneGranted": "No se otorgó ningún permiso.",
  "operator.roles.catalogueFailed":
    "No se pudo cargar el catálogo de permisos, así que cada permiso se muestra por su clave.",
  "operator.roles.reassignPrompt":
    "Asignado actualmente a {count}. Muévelos a otro rol antes de eliminar.",
  "operator.roles.reassignTo": "Moverlos a",
  "operator.roles.chooseRole": "Elige un rol",
  "operator.roles.reassignConfirm": "Mover y eliminar",

  "operator.audit.title": "Registro de auditoría",
  "operator.audit.description":
    "Cada acción de operador que movió dinero o cambió un precio. Solo se agrega: no hay endpoint de escritura.",
  "operator.audit.empty": "Todavía no hay nada registrado.",
  "operator.audit.when": "Cuándo",
  "operator.audit.who": "Quién",
  "operator.audit.action": "Acción",
  "operator.audit.entity": "Entidad",
  "operator.audit.changedFields": "Campos modificados",

  "chainStatus.active": "Activo",
  "chainStatus.suspended": "Suspendido",
  "chainStatus.invalidated": "Anulado",

  "storeStatus.active": "Activo",
  "storeStatus.suspended": "Suspendido",
  "storeStatus.closed": "Cerrado",

  "userStatus.active": "Activo",
  "userStatus.disabled": "Deshabilitado",

  "partnerStatus.active": "Activo",
  "partnerStatus.suspended": "Suspendido",

  "planStatus.active": "Activo",
  "planStatus.invalidated": "Retirado",

  "contractStatus.draft": "Borrador",
  "contractStatus.submitted": "Enviado",
  "contractStatus.active": "Activo",
  "contractStatus.completed": "Completado",
  "contractStatus.cancelled": "Cancelado",

  "billingScope.store": "Local",
  "billingScope.chain": "Cadena",

  "productKind.plan": "Plan",
  "productKind.addon": "Complemento",
  "productKind.hardware": "Equipo",
  "productKind.package": "Paquete",

  "transactionSettlementStatus.pending": "Pendiente",
  "transactionSettlementStatus.settled": "Liquidada",

  "partnerSettlementStatus.draft": "Borrador",
  "partnerSettlementStatus.pending_audit": "Pendiente de auditoría",
  "partnerSettlementStatus.completed": "Completada",
  "partnerSettlementStatus.failed": "Fallida",

  "referralStatus.new": "Nuevo",
  "referralStatus.contacted": "Contactado",
  "referralStatus.converted": "Convertido",
  "referralStatus.rejected": "Rechazado",

  "featureGroup.cashier": "Caja",
  "featureGroup.data": "Reportes",
  "featureGroup.store_settings": "Configuración del local",
  "featureGroup.marketing": "Marketing",
  "featureGroup.item_management": "Menú",
  "featureGroup.order_handling": "Pedidos",
  "featureGroup.settlement": "Liquidación",
  "featureGroup.inventory": "Inventario",
  "featureGroup.reservation": "Reservas",
  "featureGroup.chain": "Cadena",

  "grade.basic": "Básico",
  "grade.premium": "Premium",
  // Permission labels — role screens render the catalogue's key through here
  // rather than the catalogue's English `name`.
  "permission.items.read": "Ver los artículos",
  "permission.items.write": "Crear y editar artículos",
  "permission.items.delete": "Eliminar artículos",
  "permission.items.price": "Cambiar los precios de los artículos",
  "permission.categories.read": "Ver las categorías",
  "permission.categories.write": "Crear y editar categorías",
  "permission.categories.delete": "Eliminar categorías",
  "permission.modifiers.read": "Ver especificaciones, complementos y etiquetas",
  "permission.modifiers.write": "Editar especificaciones, complementos y etiquetas",
  "permission.modifiers.delete": "Eliminar especificaciones, complementos y etiquetas",
  "permission.tables.read": "Ver las mesas y zonas",
  "permission.tables.write": "Crear y editar mesas y zonas",
  "permission.tables.delete": "Eliminar mesas y zonas",
  "permission.orders.read": "Ver los pedidos",
  "permission.orders.write": "Tomar y modificar pedidos",
  "permission.orders.export": "Exportar pedidos",
  "permission.orders.discount": "Aplicar un descuento",
  "permission.orders.void": "Anular un pedido o un artículo",
  "permission.orders.refund": "Reembolsar",
  "permission.orders.reverse": "Revertir un cobro ya completado",
  "permission.orders.tax_exempt": "Cobrar un pedido sin impuesto",
  "permission.orders.tip_adjust": "Ajustar una propina después del cobro",
  "permission.hr.read": "Ver los turnos y las hojas de horas",
  "permission.hr.write": "Administrar turnos y corregir hojas de horas",
  "permission.hr.clock": "Marcar entrada y salida",
  "permission.hr.tip_finalise": "Finalizar un reparto de propinas",
  "permission.chain.royalty": "Fijar regalías y cobrar cargos",
  "permission.reservations.read": "Ver las reservas y la lista de espera",
  "permission.reservations.write": "Tomar reservas y sentar a los comensales",
  "permission.giftcards.read": "Consultar una tarjeta de regalo",
  "permission.giftcards.write": "Emitir y anular tarjetas de regalo",
  "permission.settlement.pay": "Cobrar",
  "permission.settlement.drawer": "Abrir el cajón del dinero",
  "permission.shifts.read": "Ver los registros de turno",
  "permission.shifts.write": "Abrir y entregar un turno",
  "permission.staff.read": "Ver el personal",
  "permission.staff.write": "Crear y editar personal",
  "permission.staff.delete": "Eliminar personal",
  "permission.roles.read": "Ver los puestos",
  "permission.roles.write": "Crear y editar puestos",
  "permission.settings.read": "Ver la configuración del local",
  "permission.settings.store": "Editar el perfil del local",
  "permission.settings.tax": "Editar las reglas de impuestos",
  "permission.settings.charges": "Editar el cargo por servicio y la tarifa de mesa",
  "permission.settings.currency": "Editar la moneda y el tipo de cambio",
  "permission.settings.payment_methods": "Editar las formas de pago",
  "permission.printers.read": "Ver las impresoras y el registro de impresión",
  "permission.printers.write": "Editar, vincular y desvincular impresoras",
  "permission.printers.reprint": "Reimprimir un documento",
  "permission.terminals.pair": "Vincular un terminal",
  "permission.terminals.assign_staff": "Asignar a alguien a un terminal",
  "permission.templates.read": "Ver las plantillas de recibo",
  "permission.templates.write": "Editar las plantillas de recibo",
  "permission.banners.read": "Ver los anuncios",
  "permission.banners.write": "Crear y editar anuncios",
  "permission.banners.delete": "Eliminar anuncios",
  "permission.marketing.read": "Ver los cupones y promociones",
  "permission.marketing.write": "Crear y editar cupones y promociones",
  "permission.members.read": "Ver los clientes",
  "permission.members.write": "Crear y editar clientes",
  "permission.members.balance": "Recargar y ajustar el saldo de un cliente",
  "permission.members.points": "Ajustar los puntos de un cliente",
  "permission.reports.read": "Ver los reportes",
  "permission.reports.export": "Exportar reportes",
  "permission.reports.cost": "Ver los reportes de costos y ganancias",
  "permission.audit.read": "Ver el registro de operaciones",
  "permission.billing.read": "Ver la suscripción",
  "permission.billing.manage": "Firmar, renovar y comprar complementos",
  "permission.chain.read": "Ver los totales de la cadena",
  "permission.chain.write": "Configurar los locales de la cadena",
  "permission.admin.merchants.read": "Ver los comercios",
  "permission.admin.merchants.write": "Crear y editar comercios",
  "permission.admin.plans.read": "Ver las versiones de plan y sus funciones",
  "permission.admin.plans.write": "Editar las versiones de plan y sus funciones",
  "permission.admin.contracts.read": "Ver los contratos",
  "permission.admin.contracts.sign": "Firmar y renovar contratos",
  "permission.admin.subscriptions.read": "Ver las suscripciones",
  "permission.admin.subscriptions.write": "Aprovisionar, mejorar y renovar suscripciones",
  "permission.admin.subscriptions.invalidate": "Anular una suscripción",
  "permission.admin.hardware.read": "Ver los equipos",
  "permission.admin.hardware.write": "Editar equipos",
  "permission.admin.partners.read": "Ver los socios de canal",
  "permission.admin.partners.write": "Editar socios de canal",
  "permission.admin.settlements.read": "Ver las liquidaciones con socios",
  "permission.admin.settlements.complete": "Completar una liquidación con un socio",
  "permission.admin.reconciliation.read": "Ver la conciliación",
  "permission.admin.team.read": "Ver el equipo de la plataforma",
  "permission.admin.team.write": "Editar el equipo de la plataforma y sus puestos",
  "permission.admin.dashboard.read": "Ver el panel de la plataforma",
  "permission.admin.referrals.read": "Ver los referidos",
  "permission.admin.referrals.write": "Actualizar el estado y la titularidad de un referido",
  "permission.psi.warehouse.read": "Ver los almacenes",
  "permission.psi.warehouse.write": "Crear y editar almacenes",
  "permission.psi.supply.read": "Ver los insumos, unidades, categorías y marcas",
  "permission.psi.supply.write": "Crear y editar insumos, unidades, categorías y marcas",
  "permission.psi.supplier.read": "Ver los proveedores",
  "permission.psi.supplier.write": "Crear y editar proveedores",
  "permission.psi.purchase.read": "Ver las compras",
  "permission.psi.purchase.write": "Crear y editar compras",
  "permission.psi.purchase.approve": "Aprobar o anular una compra",
  "permission.psi.inbound.read": "Ver las entradas",
  "permission.psi.inbound.write": "Crear y editar entradas",
  "permission.psi.inbound.approve": "Aprobar o rechazar una entrada",
  "permission.psi.outbound.read": "Ver las salidas",
  "permission.psi.outbound.write": "Crear y editar salidas",
  "permission.psi.outbound.approve": "Aprobar una salida",
  "permission.psi.count.read": "Ver los conteos de existencias",
  "permission.psi.count.write": "Realizar un conteo de existencias",
  "permission.psi.count.adjust": "Registrar un ajuste de existencias",
  "permission.psi.stock.read": "Ver los saldos y lotes de existencias",
  "permission.psi.recipe.write": "Vincular artículos del menú con insumos",
  "permission.psi.requisition.approve": "Revisar una requisición de una sucursal",

  "operationAction.billing.renew": "Suscripción renovada",
  "operationAction.campaign.create": "Campaña creada",
  "operationAction.campaign.delete": "Campaña eliminada",
  "operationAction.campaign.update": "Campaña actualizada",
  "operationAction.cancel": "Reserva cancelada",
  "operationAction.chain.capabilities_changed": "Capacidades de la sucursal modificadas",
  "operationAction.chain.menu_pushed": "Menú enviado a las sucursales",
  "operationAction.chain.requisition_approved": "Requisición aprobada",
  "operationAction.chain.requisition_rejected": "Requisición rechazada",
  "operationAction.chain.store_added": "Sucursal agregada a la cadena",
  "operationAction.chain.user_access_changed": "Acceso a sucursales modificado",
  "operationAction.cost.create": "Costo agregado",
  "operationAction.cost.delete": "Costo eliminado",
  "operationAction.cost.update": "Costo actualizado",
  "operationAction.coupon.issue": "Cupón emitido",
  "operationAction.coupon_template.copy": "Plantilla de cupón copiada",
  "operationAction.coupon_template.create": "Plantilla de cupón creada",
  "operationAction.coupon_template.update": "Plantilla de cupón actualizada",
  "operationAction.create": "Reserva creada",
  "operationAction.gift_card.issue": "Tarjeta de regalo emitida",
  "operationAction.gift_card.top_up": "Tarjeta de regalo recargada",
  "operationAction.gift_card.void": "Tarjeta de regalo anulada",
  "operationAction.inventory.inbound_approved": "Ticket de entrada aprobado",
  "operationAction.inventory.inbound_rejected": "Ticket de entrada rechazado",
  "operationAction.inventory.outbound_issued": "Orden de salida emitida",
  "operationAction.inventory.purchase_notified": "Orden de compra enviada al proveedor",
  "operationAction.inventory.purchase_raised": "Orden de compra creada",
  "operationAction.inventory.purchase_received": "Orden de compra recibida",
  "operationAction.inventory.purchase_voided": "Orden de compra anulada",
  "operationAction.inventory.recipe_changed": "Receta del artículo modificada",
  "operationAction.inventory.stock_adjusted": "Existencias ajustadas",
  "operationAction.inventory.supplier_prices_changed": "Precios del proveedor modificados",
  "operationAction.inventory.transfer_accepted": "Transferencia aceptada",
  "operationAction.inventory.transfer_rejected": "Transferencia rechazada",
  "operationAction.item.batch": "Artículos actualizados en lote",
  "operationAction.item.create": "Artículo agregado",
  "operationAction.item.delete": "Artículo eliminado",
  "operationAction.item.meal_set_changed": "Combo de comida modificado",
  "operationAction.item.update": "Artículo actualizado",
  "operationAction.job.create": "Puesto de trabajo agregado",
  "operationAction.job.delete": "Puesto de trabajo eliminado",
  "operationAction.job.update": "Puesto de trabajo actualizado",
  "operationAction.no_show": "Reserva marcada como no presentado",
  "operationAction.notifications.update": "Configuración de avisos actualizada",
  "operationAction.payment_method.create": "Forma de pago agregada",
  "operationAction.payment_method.delete": "Forma de pago eliminada",
  "operationAction.print.reprint": "Recibo reimpreso",
  "operationAction.printer.bind": "Impresora asignada",
  "operationAction.printer.create": "Impresora agregada",
  "operationAction.printer.unbind": "Impresora desasignada",
  "operationAction.promotion.create": "Promoción creada",
  "operationAction.promotion.delete": "Promoción eliminada",
  "operationAction.promotion.update": "Promoción actualizada",
  "operationAction.receipt_template.update": "Plantilla de recibo actualizada",
  "operationAction.role.create": "Puesto creado",
  "operationAction.role.update": "Puesto actualizado",
  "operationAction.royalty.run": "Regalías calculadas",
  "operationAction.royalty_charge.finalise": "Cargo de regalías finalizado",
  "operationAction.royalty_scheme.create": "Esquema de regalías creado",
  "operationAction.royalty_scheme.set_stores": "Sucursales del esquema de regalías modificadas",
  "operationAction.royalty_scheme.update": "Esquema de regalías actualizado",
  "operationAction.seat": "Grupo sentado",
  "operationAction.service_charge_rate.create": "Tarifa de cargo por servicio agregada",
  "operationAction.service_charge_rate.delete": "Tarifa de cargo por servicio eliminada",
  "operationAction.service_charge_rate.update": "Tarifa de cargo por servicio actualizada",
  "operationAction.settings.delivery_providers_changed": "Proveedores de entrega modificados",
  "operationAction.settings.invoice_changed": "Numeración de facturas modificada",
  "operationAction.settlement.drawer_opened": "Cajón de dinero abierto",
  "operationAction.shift.close": "Turno cerrado",
  "operationAction.specification_group.create": "Grupo de especificaciones creado",
  "operationAction.specification_group.delete": "Grupo de especificaciones eliminado",
  "operationAction.specification_group.update": "Grupo de especificaciones actualizado",
  "operationAction.staff.create": "Empleado agregado",
  "operationAction.staff.delete": "Empleado eliminado",
  "operationAction.staff.photo_delete": "Foto del empleado eliminada",
  "operationAction.staff.photo_set": "Foto del empleado establecida",
  "operationAction.staff.reset_pin": "PIN del empleado restablecido",
  "operationAction.staff.take_till": "Caja tomada por escaneo",
  "operationAction.staff.assign_till": "Asignado a un terminal por un gerente",
  "operationAction.staff.update": "Empleado actualizado",
  "operationAction.staff.set_password": "Contraseña del empleado establecida",
  "operationAction.store_group.create": "Grupo de sucursales creado",
  "operationAction.store_group.delete": "Grupo de sucursales eliminado",
  "operationAction.store_group.set_stores": "Sucursales del grupo modificadas",
  "operationAction.store_group.update": "Grupo de sucursales actualizado",
  "operationAction.store_profile.update": "Perfil de la sucursal actualizado",
  "operationAction.table_fee.update": "Cargo por mesa actualizado",
  "operationAction.tax_rate.create": "Tasa de impuesto agregada",
  "operationAction.tax_rate.delete": "Tasa de impuesto eliminada",
  "operationAction.tax_rate.update": "Tasa de impuesto actualizada",
  "operationAction.tax_settings.update": "Configuración de impuestos actualizada",
  "operationAction.time_entry.correct": "Registro de horario corregido",
  "operationAction.tip_distribution.finalise": "Distribución de propinas finalizada",
  "operationAction.tip_pool.create": "Fondo de propinas creado",
  "operationAction.tip_pool.delete": "Fondo de propinas eliminado",
  "operationAction.tip_pool.run": "Fondo de propinas calculado",
  "operationAction.tip_pool.set_jobs": "Puestos del fondo de propinas modificados",
  "operationAction.tip_pool.update": "Fondo de propinas actualizado",
  "operationAction.tip_settings.update": "Configuración de propinas actualizada",
  "operationAction.topping_template.create": "Plantilla de complementos creada",
  "operationAction.topping_template.delete": "Plantilla de complementos eliminada",
  "operationAction.topping_template.update": "Plantilla de complementos actualizada",
  "operationAction.topup_tier.create": "Nivel de recarga creado",
  "operationAction.topup_tier.delete": "Nivel de recarga eliminado",
  "operationAction.trading_config.update": "Horario de operación actualizado",
};

/**
 * Every key in this pack is machine-drafted and awaits native review, on the
 * same terms as [`KM_AWAITING_REVIEW`](km.ts). A reviewer removes keys from
 * here as they sign each one off.
 */
export const ES_AWAITING_REVIEW: readonly string[] = Object.keys(es).filter(
  (key) => key !== "app.name",
);
